import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { RepeatIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { useCrossVariantStatus } from '@/hooks/useCrossVariantStatus';
import {
  useVariantValidationStatus,
  ValidationStatusIcon,
} from '@/hooks/useVariantValidationStatus';
import {
  getCampaignAssets,
  submitCampaignAssets,
  deleteCampaignAsset,
  deleteCampaignAssets,
  updateCampaign,
  deleteVariant as deleteVariantApi,
  approveAllVariants,
  validateVariant,
  submitAIFeedback,
  type CampaignAsset,
  type UploadedFileRef,
  type Campaign,
  type ValidationFeedback,
  type AssetOrderToken,
} from '@/api/campaigns';
import { authenticatedFetch } from '@/api/client';
import { createMediaBlobUrl } from '@/utils/blobMediaType';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useUploadProgress,
  UploadProgressModal,
  type UploadFileEntry,
} from '@/components/UploadProgressModal';
import { type MockupType, STORY_FORMAT_TYPES, MOCKUP_OPTIONS } from '../types';
import { type DCItem } from './ad-details/MetaDynamicCreativeDetails';
import { OfferLedSection } from './ad-details/OfferLedSection';
import type { MetaDCInitialValues } from './ad-details/MetaDynamicCreativeDetails';
import type {
  MetaCarouselFeedUploads,
  MetaCarouselInitialItem,
} from './ad-details/MetaCarouselDetails';
import type { LinkedInCarouselInitialItem } from './ad-details/LinkedInCarouselDetails';
import { type FeedRatio } from './ad-details/feedCardSlots';
import type {
  FlexibleInitialCard,
  FlexibleCardState,
  MetaFlexibleInitialValues,
} from './ad-details/MetaFlexibleAdDetails';
import type { FieldIssue } from '../types';
import {
  normalizeRatio,
  exactRatio,
  resolveEffectiveMockupType,
  FORMAT_RATIOS,
  RATIO_LABELS,
  normalizeCta,
  type Ratio,
  type UploadRatio,
} from '../utils';
import { MockupPreview } from './MockupPreview';
import { AdDetailsContent } from './AdDetailsContent';
import { assetsContentSignature } from './assetsContentSignature';
import { cardsHaveFiles } from './cardsHaveFiles';
import { staleIssueKeys } from './staleIssueKeys';
import { useGearExchangeSearch } from '@/hooks/useGearExchangeSearch';

/**
 * Display-order comparator for campaign assets.
 *
 * Mirrors the backend ordering (`position ASC NULLS LAST, created_at ASC`) so the
 * UI shows the same order the API returns. `position` is the persisted, explicit
 * order the user arranged; `created_at` is only a fallback for legacy rows that
 * never got a position (the migration backfilled existing rows, so in practice
 * this only matters for rows written by an older backend).
 */
function byDisplayOrder(a: CampaignAsset, b: CampaignAsset): number {
  const ap = a.position ?? null;
  const bp = b.position ?? null;
  if (ap !== null && bp !== null && ap !== bp) return ap - bp;
  if (ap !== null && bp === null) return -1; // nulls last
  if (ap === null && bp !== null) return 1;
  return (a.created_at ?? '').localeCompare(b.created_at ?? '');
}

/**
 * Lay a ratio's resolved rows out by card index for the two-crop feed carousels.
 *
 * Crops are placed by their stored `position`, which the save flow sets to the
 * card index (that is what the nulls in asset_order buy us). Pairing by array
 * index instead would be wrong the moment a card is missing a crop: if only card
 * 3 has a 4:5, it is the sole row in its lane and index 0 would hang it off card
 * 1.
 *
 * Rows predating that scheme have no usable position, so fall back to sequential
 * placement — the old behaviour, and correct for the fully populated lanes it
 * produced.
 */
function byCardPosition<T extends { position: number | null }>(
  results: (T | null)[],
): (T | null)[] {
  const out: (T | null)[] = [];
  const usePosition = results.every((r) => !r || r.position !== null);
  results.forEach((r, i) => {
    if (!r) return;
    out[usePosition && r.position !== null ? r.position : i] = r;
  });
  // Assigning by index leaves holes, which read back as `undefined` rather than
  // the `null` the signature promises. Densify so a gap for a card that has no
  // crop in this lane is an explicit null.
  return Array.from({ length: out.length }, (_, i) => out[i] ?? null);
}

// ─── Main component ───────────────────────────────────────────────────────────

interface PreviewAndAdDetailsProps {
  mockupType: MockupType;
  showErrors: boolean;
  showWarnings: boolean;
  formatLabel?: string;
  onBack?: () => void;
  campaignId?: string;
  campaign?: Campaign;
  /** Field issues from the last validation run — passed in from parent so they survive re-renders */
  fieldIssues?: Record<string, FieldIssue>;
  /** Ref for the save handler — header Save button calls this */
  saveFnRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
  /** Ref for the validate handler — header Validate button calls this */
  validateFnRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
  /** Called whenever dirty state changes — parent uses this to enable/grey the header Save button */
  onDirtyChange?: (dirty: boolean) => void;
  /** Called when a save starts/ends — parent uses this to show a spinner on the header Save button */
  onSavingChange?: (saving: boolean) => void;
  /** Called when validation starts/ends — parent uses this to show a spinner on the Validate button */
  onValidatingChange?: (validating: boolean) => void;
  /** Called after validation completes with the mapped field issues */
  onValidationResult?: (issues: Record<string, FieldIssue>) => void;
  /**
   * Called with the fieldIssues keys whose underlying data has just changed, so
   * the parent can drop findings that no longer describe what is on screen.
   */
  onFieldsChanged?: (keys: string[]) => void;
  /** Called after validation so the parent can auto-enable the error/warning toggles */
  onValidationComplete?: (hasErrors: boolean, hasWarnings: boolean) => void;
  /** Ref for the approve handler — header Approve button calls this with an optional note */
  approveFnRef?: React.MutableRefObject<((note: string) => Promise<void>) | undefined>;
  /** Called when approval starts/ends — parent uses this to show a spinner */
  onApprovingChange?: (approving: boolean) => void;
  /** Called when hasValidated changes so the header can enable/disable the Approve button */
  onHasValidatedChange?: (hasValidated: boolean) => void;
  /** Called when the current variant's approved state changes */
  onIsApprovedChange?: (isApproved: boolean) => void;
  /** Called when any variant has validation errors (used to grey out Approve) */
  onAnyVariantHasErrorsChange?: (hasErrors: boolean) => void;
  /** When true, disable all form inputs and hide add/delete variant buttons */
  readOnly?: boolean;
  /** Effective asset type ID — falls back to the single available type when campaign.asset_type_id hasn't been persisted yet */
  effectiveAssetTypeId?: string;
  /** Called after all assets are deleted — parent should clear local state and refetch */
  onDeleteAll?: () => void;
}

const MAX_VARIANTS = 8;
/** Recommended minimum number of variants for optimal ad performance */
const RECOMMENDED_MIN_VARIANTS = 3;

// normalizeCta lives in ../utils to keep this file component-only (required for React Fast Refresh)

type VariantState = { number: number; saved: boolean };

export function PreviewAndAdDetails({
  mockupType,
  showErrors,
  showWarnings,
  fieldIssues: fieldIssuesProp,
  formatLabel: formatLabelProp,
  onBack,
  campaignId,
  campaign,
  effectiveAssetTypeId,
  saveFnRef,
  validateFnRef,
  approveFnRef,
  onDirtyChange,
  onSavingChange,
  onValidatingChange,
  onApprovingChange,
  onValidationResult,
  onFieldsChanged,
  onValidationComplete,
  onHasValidatedChange,
  onIsApprovedChange,
  onAnyVariantHasErrorsChange,
  readOnly,
  onDeleteAll,
}: PreviewAndAdDetailsProps) {
  const queryClient = useQueryClient();
  const {
    uploadFiles,
    uploadProgress,
    isUploading,
    showUploadProgress,
    dismiss: dismissUpload,
    cancel: cancelUpload,
  } = useUploadProgress();

  // Mirrors what we report via onValidatingChange, but kept locally so this
  // component can disable its own controls (e.g. the upload modal's close
  // button) while validation runs. The upload modal outlives the uploads
  // themselves during a Validate, since handleValidate saves first.
  const [isValidatingLocal, setIsValidatingLocal] = useState(false);

  // Use the effective asset type ID (which has the single-type fallback) so uploads/saves
  // always have an asset_type_id even before the auto-select mutation has persisted it.
  const assetTypeId = effectiveAssetTypeId ?? campaign?.asset_type_id ?? undefined;

  /** Upload files with progress tracking. Drop-in replacement for Promise.all(files.map(uploadSingleFile)). */
  const uploadWithProgress = useCallback(
    async (
      files: {
        file: File;
        ratio?: string;
        assetTypeId?: string;
        existingAssetId?: string;
      }[],
    ): Promise<UploadedFileRef[]> => {
      if (!campaignId || files.length === 0) return [];
      const entries: UploadFileEntry[] = files.map((f) => ({
        file: f.file,
        ratio: f.ratio,
        assetTypeId: f.assetTypeId,
        existingAssetId: f.existingAssetId,
      }));
      const { refs, failures } = await uploadFiles(campaignId, entries);
      if (failures.length > 0) {
        throw new Error(`${failures.length} file(s) failed to upload`);
      }
      return refs;
    },
    [campaignId, uploadFiles],
  );

  // Resolve an ordered token list (existing ids + new-file indexes) into the flat
  // `asset_order` array of real asset IDs the backend expects. `newFileIndex` is
  // resolved via the corresponding UploadedFileRef (uploadWithProgress throws on
  // any failed upload, so `refs[]` stays aligned with the input file order).
  const buildAssetOrder = useCallback(
    (order: AssetOrderToken[], refs: UploadedFileRef[]): (string | null)[] => {
      const ids: (string | null)[] = [];
      for (const token of order) {
        if ('existingAssetId' in token) {
          ids.push(token.existingAssetId);
        } else if ('empty' in token) {
          // Hold the slot so everything after it keeps its index.
          ids.push(null);
        } else {
          const ref = refs[token.newFileIndex];
          // A ref that failed to resolve becomes a gap rather than a silent shift
          // left, which would drag every later row onto the wrong position.
          ids.push(ref?.asset_id ?? null);
        }
      }
      return ids;
    },
    [],
  );
  // Variant numbers added locally but not yet saved to DB
  const [unsavedNewVariants, setUnsavedNewVariants] = useState<number[]>([]);
  const [selectedVariant, setSelectedVariant] = useState(1);
  const prevVariantRef = useRef(selectedVariant);
  const isSavingRef = useRef(false);
  const [selectedRatio, setSelectedRatio] = useState<Ratio>('1x1');
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [file1x1, setFile1x1] = useState<File | null>(null);
  const [file4x5, setFile4x5] = useState<File | null>(null);
  const [file9x16, setFile9x16] = useState<File | null>(null);
  // Lifted state for Meta Dynamic Creative — active asset URLs and active text values for mockup
  const [metaDCImageUrl1x1, setMetaDCImageUrl1x1] = useState<string | undefined>(undefined);
  const [metaDCImageUrl9x16, setMetaDCImageUrl9x16] = useState<string | undefined>(undefined);
  const [metaDCCaption, setMetaDCCaption] = useState('');
  const [metaDCHeadline, setMetaDCHeadline] = useState('');
  const [metaDCDescription, setMetaDCDescription] = useState('');
  const [metaDCCta, setMetaDCCta] = useState('Learn More');
  // DC save state — full text arrays, URL, legal, new files, deleted IDs, loaded initial assets
  const [metaDCPrimaryTexts, setMetaDCPrimaryTexts] = useState<string[]>(['']);
  const [metaDCHeadlines, setMetaDCHeadlines] = useState<string[]>(['']);
  const [metaDCDescriptions, setMetaDCDescriptions] = useState<string[]>(['']);
  const [metaDCCtas, setMetaDCCtas] = useState<string[]>(['Learn More']);
  const [metaDCUrl, setMetaDCUrl] = useState('');
  const [metaDCLegal, setMetaDCLegal] = useState('');
  const [metaDCNewFiles1x1, setMetaDCNewFiles1x1] = useState<File[]>([]);
  const [metaDCNewFiles4x5, setMetaDCNewFiles4x5] = useState<File[]>([]);
  const [metaDCNewFiles9x16, setMetaDCNewFiles9x16] = useState<File[]>([]);
  const [metaDCDeletedIds1x1, setMetaDCDeletedIds1x1] = useState<string[]>([]);
  const [metaDCDeletedIds4x5, setMetaDCDeletedIds4x5] = useState<string[]>([]);
  const [metaDCDeletedIds9x16, setMetaDCDeletedIds9x16] = useState<string[]>([]);
  const [metaDCOrder1x1, setMetaDCOrder1x1] = useState<AssetOrderToken[]>([]);
  const [metaDCOrder4x5, setMetaDCOrder4x5] = useState<AssetOrderToken[]>([]);
  const [metaDCOrder9x16, setMetaDCOrder9x16] = useState<AssetOrderToken[]>([]);
  // Resolved initial items from DB (for restoring the asset grids on page load)
  const [metaDCInitial1x1, setMetaDCInitial1x1] = useState<DCItem[]>([]);
  const [metaDCInitial4x5, setMetaDCInitial4x5] = useState<DCItem[]>([]);
  const [metaDCInitial9x16, setMetaDCInitial9x16] = useState<DCItem[]>([]);
  // Lifted state for Meta Flexible Ad
  const [flexibleImageUrl, setFlexibleImageUrl] = useState<string | undefined>(undefined);
  const [flexibleCaption, setFlexibleCaption] = useState('');
  const [flexibleHeadline, setFlexibleHeadline] = useState('');
  const [flexibleDescription, setFlexibleDescription] = useState('');
  const [flexibleCta, setFlexibleCta] = useState('Learn More');
  const [flexibleUrl, setFlexibleUrl] = useState('');
  const [flexibleLegal, setFlexibleLegal] = useState('');
  const [flexibleCards, setFlexibleCards] = useState<FlexibleCardState[]>([]);
  const [flexibleDeletedIds, setFlexibleDeletedIds] = useState<string[]>([]);
  const [flexibleInitialCards, setFlexibleInitialCards] = useState<FlexibleInitialCard[]>([]);
  // Lifted state for Meta Collection — shared products/copy; hero images reuse file1x1/file9x16 + apiImageUrl1x1/9x16
  const [collectionCaption, setCollectionCaption] = useState('');
  const [collectionCta, setCollectionCta] = useState('Shop Now');
  const [collectionUrl, setCollectionUrl] = useState('');
  const [collectionLegal, setCollectionLegal] = useState('');
  const [collectionProducts, setCollectionProducts] = useState<
    { imageUrl?: string; title?: string }[]
  >([]);
  const [collectionWebsite, setCollectionWebsite] = useState('');
  // Product files to upload + IDs to delete on next save
  const [collectionProductFiles, setCollectionProductFiles] = useState<(File | null)[]>([]);
  const [collectionProductDeletedIds, setCollectionProductDeletedIds] = useState<string[]>([]);
  // AI feedback state — keyed by warning key (rule::field::index)
  const [aiFeedback, setAiFeedback] = useState<Record<string, ValidationFeedback>>({});
  const [feedbackPending, setFeedbackPending] = useState<Set<string>>(new Set());
  // Resolved product items from DB (for restoring thumbnails and titles on page load)
  const [collectionInitialProducts, setCollectionInitialProducts] = useState<
    { title?: string; imageUrl?: string; existingAssetId?: string }[]
  >([]);
  // Lifted state for Meta single image/video — wired live into all three ratio mockups
  const [metaCaption, setMetaCaption] = useState('');
  const [metaHeadline, setMetaHeadline] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaCta, setMetaCta] = useState('Learn More');
  // Save payload state — all form fields needed to POST to the backend
  const [metaAllPostCopies, setMetaAllPostCopies] = useState<string[]>(['']);
  const [metaUrl, setMetaUrl] = useState('');
  const [metaLegal, setMetaLegal] = useState('');
  // Offer-led metadata — per variant, shared by every ad format (rendered once
  // above AdDetailsContent rather than per format).
  const [isOfferLed, setIsOfferLed] = useState(false);
  const [offerEndDate, setOfferEndDate] = useState('');
  // Asset IDs to delete on the next save — populated when the user confirms image deletion.
  // Tracked separately because cleared images are invisible to the loadedValues comparison.
  const [pendingDeleteAssetIds, setPendingDeleteAssetIds] = useState<string[]>([]);
  // Snapshot of values as they were when last loaded from the API or successfully saved.
  // isDirty is derived by comparing current state against this — no callbacks, no timing tricks.
  const [loadedValues, setLoadedValues] = useState<{
    postCopies: string[];
    headline: string;
    description: string;
    cta: string;
    url: string;
    legal: string;
    isOfferLed: boolean;
    offerEndDate: string;
    carouselHeadlines?: string[];
    carouselDestUrls?: string[];
    dcHeadlines?: string[];
    dcDescriptions?: string[];
    dcCtas?: string[];
  } | null>(null);
  // Lifted state for Meta carousel — separate feed (1:1) and story (9:16) card sets
  const [metaCarouselCaption, setMetaCarouselCaption] = useState('');
  const [metaCarouselLegal, setMetaCarouselLegal] = useState('');
  const [metaCarouselFeedItems, setMetaCarouselFeedItems] = useState<
    {
      imageUrl?: string;
      contentType?: string | null;
      imageUrl4x5?: string;
      contentType4x5?: string | null;
      headline?: string;
      description?: string;
    }[]
  >([]);
  // Which crop of the feed cards the mockup shows. A card carries a 1:1 and a 4:5
  // rendering of itself and only one can be on screen, so clicking a thumbnail
  // picks it. Mirrors `feedPreviewSlot` in the single-image formats.
  const [metaCarouselFeedSlot, setMetaCarouselFeedSlot] = useState<'1x1' | '4x5'>('1x1');
  const [metaCarouselStoryItems, setMetaCarouselStoryItems] = useState<{ imageUrl?: string }[]>([]);
  const [metaCarouselFeedActiveCard, setMetaCarouselFeedActiveCard] = useState(0);
  const [metaCarouselStoryActiveCard, setMetaCarouselStoryActiveCard] = useState(0);
  // Shared destination URL and CTA for all carousel cards
  const [metaCarouselDestinationUrl, setMetaCarouselDestinationUrl] = useState('');
  const [metaCarouselCta, setMetaCarouselCta] = useState('Learn More');
  // Card save state: new files, deleted IDs, total count, per-card data — feed (1:1) and story (9:16)
  const [metaCarouselFeedNewFiles, setMetaCarouselFeedNewFiles] = useState<File[]>([]);
  const [metaCarouselFeedDeletedIds, setMetaCarouselFeedDeletedIds] = useState<string[]>([]);
  const [metaCarouselFeedCardData, setMetaCarouselFeedCardData] = useState<
    { headline: string; description: string }[]
  >([]);
  const [metaCarouselStoryNewFiles, setMetaCarouselStoryNewFiles] = useState<File[]>([]);
  const [metaCarouselStoryDeletedIds, setMetaCarouselStoryDeletedIds] = useState<string[]>([]);
  // The 4:5 crops of the feed cards. Held apart from the 1:1 ones because each
  // ratio is uploaded and ordered in its own submit — same split as feed/story.
  // Deletions are NOT split: a delete is a delete regardless of crop, so
  // `metaCarouselFeedDeletedIds` covers both.
  const [metaCarouselFeed4x5NewFiles, setMetaCarouselFeed4x5NewFiles] = useState<File[]>([]);
  const [metaCarouselFeed4x5Order, setMetaCarouselFeed4x5Order] = useState<AssetOrderToken[]>([]);
  // Full display order tokens (existing ids + new-file indexes) per ratio, used to
  // build asset_order on save so reorders of existing images persist.
  const [metaCarouselFeedOrder, setMetaCarouselFeedOrder] = useState<AssetOrderToken[]>([]);
  const [metaCarouselStoryOrder, setMetaCarouselStoryOrder] = useState<AssetOrderToken[]>([]);
  // Resolved card items from DB (for restoring card strips on page load)
  const [metaCarouselFeedResolvedCards, setMetaCarouselFeedResolvedCards] = useState<
    MetaCarouselInitialItem[]
  >([]);
  const [metaCarouselStoryResolvedCards, setMetaCarouselStoryResolvedCards] = useState<
    {
      imageUrl: string;
      existingAssetId: string;
      contentType?: string | null;
    }[]
  >([]);

  // Stable callbacks for meta-carousel card changes (prevents unnecessary re-renders in CardSection)
  const handleMetaCarouselFeedCardsChange = useCallback(
    (
      uploads: MetaCarouselFeedUploads,
      deletedIds: string[],
      _totalCount: number,
      cardData: { headline: string; description: string }[],
    ) => {
      setMetaCarouselFeedNewFiles(uploads['1x1'].newFiles);
      setMetaCarouselFeedOrder(uploads['1x1'].order);
      setMetaCarouselFeed4x5NewFiles(uploads['4x5'].newFiles);
      setMetaCarouselFeed4x5Order(uploads['4x5'].order);
      setMetaCarouselFeedDeletedIds(deletedIds);
      setMetaCarouselFeedCardData(cardData);
    },
    [],
  );
  const handleMetaCarouselStoryCardsChange = useCallback(
    (
      newFiles: File[],
      deletedIds: string[],
      _totalCount: number,
      _cardData: { headline: string; description: string }[],
      order: AssetOrderToken[],
    ) => {
      setMetaCarouselStoryNewFiles(newFiles);
      setMetaCarouselStoryDeletedIds(deletedIds);
      setMetaCarouselStoryOrder(order);
    },
    [],
  );

  // Lifted copy fields for formats that wire text live into the mockup
  const [snapchatHeadline, setSnapchatHeadline] = useState('');
  const [snapchatUrl, setSnapchatUrl] = useState('');
  const [snapchatLegal, setSnapchatLegal] = useState('');
  // Story state — headline/items passed to SnapchatStoryAdMockup; activeCard synced from mockup navigation
  const [snapchatStoryHeadline, setSnapchatStoryHeadline] = useState('');
  const [snapchatStoryItems, setSnapchatStoryItems] = useState<
    { imageUrl?: string; headline: string }[]
  >([]);
  const [snapchatActiveCard, setSnapchatActiveCard] = useState(0);
  // Lifted state for LinkedIn single image
  const [linkedInFile, setLinkedInFile] = useState<File | null>(null);
  // LinkedIn keeps its own 4:5 slot rather than reusing Meta's `file4x5`: the two
  // formats never render together, but they have separate save branches and
  // separate reset points, so sharing one state would leak a file across a format
  // switch.
  const [linkedInFile4x5, setLinkedInFile4x5] = useState<File | null>(null);
  const [linkedInCaption, setLinkedInCaption] = useState('');
  const [linkedInHeadline, setLinkedInHeadline] = useState('');
  const [linkedInCta, setLinkedInCta] = useState('Learn More');
  const [linkedInWebsite, setLinkedInWebsite] = useState('');
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [linkedInLegal, setLinkedInLegal] = useState('');
  // Lifted state for LinkedIn document
  const [linkedInDocFile, setLinkedInDocFile] = useState<File | null>(null);
  const [linkedInDocCaption, setLinkedInDocCaption] = useState('');
  const [linkedInDocHeadline, setLinkedInDocHeadline] = useState('');
  const [linkedInDocPageImage, setLinkedInDocPageImage] = useState<string | null>(null);
  const [linkedInDocUrl, setLinkedInDocUrl] = useState('');
  const [linkedInDocLegal, setLinkedInDocLegal] = useState('');
  // Lifted state for LinkedIn carousel
  const [linkedInCarouselCaption, setLinkedInCarouselCaption] = useState('');
  const [linkedInCarouselLegal, setLinkedInCarouselLegal] = useState('');
  const [linkedInCarouselItems, setLinkedInCarouselItems] = useState<
    { imageUrl?: string; imageUrl4x5?: string; headline: string }[]
  >([]);
  const [linkedInCarouselActiveCard, setLinkedInCarouselActiveCard] = useState(0);
  // Which crop the LinkedIn carousel mockup shows. Both crops share one mockup,
  // exactly as on the Meta carousel.
  const [linkedInCarouselFeedSlot, setLinkedInCarouselFeedSlot] = useState<FeedRatio>('1x1');
  // Card save state: new files to upload, existing card IDs to delete, total count, per-card data
  const [linkedInCarouselNewFiles, setLinkedInCarouselNewFiles] = useState<File[]>([]);
  const [linkedInCarouselOrder, setLinkedInCarouselOrder] = useState<AssetOrderToken[]>([]);
  const [linkedInCarousel4x5NewFiles, setLinkedInCarousel4x5NewFiles] = useState<File[]>([]);
  const [linkedInCarousel4x5Order, setLinkedInCarousel4x5Order] = useState<AssetOrderToken[]>([]);
  const [linkedInCarouselDeletedIds, setLinkedInCarouselDeletedIds] = useState<string[]>([]);
  const [, setLinkedInCarouselTotalCount] = useState(0);
  const [linkedInCarouselCardData, setLinkedInCarouselCardData] = useState<
    { headline: string; destinationUrl: string }[]
  >([]);
  // Resolved card items from DB (for restoring the card strip on page load)
  const [linkedInCarouselResolvedCards, setLinkedInCarouselResolvedCards] = useState<
    LinkedInCarouselInitialItem[]
  >([]);
  // Lifted state for TikTok
  const [tikTokCaption, setTikTokCaption] = useState('');
  const [tikTokCta, setTikTokCta] = useState('Learn More');
  const [tikTokUrl, setTikTokUrl] = useState('');
  const [tikTokLegal, setTikTokLegal] = useState('');
  // Lifted state for Snapchat Story
  const [snapchatStoryUrl, setSnapchatStoryUrl] = useState('');
  const [snapchatStoryLegal, setSnapchatStoryLegal] = useState('');
  // Card save state: new files to upload, existing card IDs to delete, total card count
  const [snapchatStoryNewFiles, setSnapchatStoryNewFiles] = useState<File[]>([]);
  const [snapchatStoryDeletedIds, setSnapchatStoryDeletedIds] = useState<string[]>([]);
  const [snapchatStoryOrder, setSnapchatStoryOrder] = useState<AssetOrderToken[]>([]);
  const [snapchatStoryTotalCount, setSnapchatStoryTotalCount] = useState(0);
  // Resolved card items from DB (for restoring the card strip on page load)
  const [snapchatStoryResolvedCards, setSnapchatStoryResolvedCards] = useState<
    { imageUrl: string; contentType?: string | null; existingAssetId: string }[]
  >([]);

  // Per-card image presence for the three card formats, sent as `carousel_items[].hasFile`
  // so the backend can flag a card that carries text but no image. Each format
  // passes every ratio lane it supports — a card counts as having an image if any
  // lane holds one (a Meta/LinkedIn card with only a 4:5 crop is still filled).
  const metaCarouselFeedHasFiles = useMemo(
    () =>
      cardsHaveFiles(
        metaCarouselFeedCardData.length,
        metaCarouselFeedOrder,
        metaCarouselFeed4x5Order,
      ),
    [metaCarouselFeedCardData.length, metaCarouselFeedOrder, metaCarouselFeed4x5Order],
  );
  const linkedInCarouselHasFiles = useMemo(
    () =>
      cardsHaveFiles(
        linkedInCarouselCardData.length,
        linkedInCarouselOrder,
        linkedInCarousel4x5Order,
      ),
    [linkedInCarouselCardData.length, linkedInCarouselOrder, linkedInCarousel4x5Order],
  );
  const snapchatStoryHasFiles = useMemo(
    () => cardsHaveFiles(snapchatStoryTotalCount, snapchatStoryOrder),
    [snapchatStoryTotalCount, snapchatStoryOrder],
  );

  // How many assets each ratio lane currently holds, per format. The count rules
  // ("less than 2 images", "less than 3 cards with an image") are all keyed off
  // these, so a change here is exactly what makes such a finding stale.
  const laneCounts = useMemo(() => {
    const filled = (lane: AssetOrderToken[]) => lane.filter((t) => !('empty' in t)).length;
    return {
      '1x1': filled(metaDCOrder1x1) + filled(metaCarouselFeedOrder) + filled(linkedInCarouselOrder),
      '4x5':
        filled(metaDCOrder4x5) +
        filled(metaCarouselFeed4x5Order) +
        filled(linkedInCarousel4x5Order),
      '9x16': filled(metaDCOrder9x16) + filled(snapchatStoryOrder),
    };
  }, [
    metaDCOrder1x1,
    metaDCOrder4x5,
    metaDCOrder9x16,
    metaCarouselFeedOrder,
    metaCarouselFeed4x5Order,
    linkedInCarouselOrder,
    linkedInCarousel4x5Order,
    snapchatStoryOrder,
  ]);

  // Retire count findings as soon as their lane changes.
  //
  // Skips the first run: mounting/hydration populates the lanes from the API and
  // would otherwise wipe the very issues just loaded from the stored validation
  // result before the user has done anything.
  const prevLaneCounts = useRef(laneCounts);
  useEffect(() => {
    const prev = prevLaneCounts.current;
    prevLaneCounts.current = laneCounts;
    if (prev === laneCounts) return;

    const stale = staleIssueKeys(prev, laneCounts);
    if (stale.length > 0) onFieldsChanged?.(stale);
  }, [laneCounts, onFieldsChanged]);

  // ── Fetch real assets from API ────────────────────────────────────────────
  const { data: assetsData } = useQuery({
    queryKey: ['campaign-assets', campaignId],
    queryFn: () => getCampaignAssets(campaignId!, true),
    enabled: !!campaignId,
  });

  const allAssets = useMemo(() => assetsData?.assets ?? [], [assetsData]);

  // Drives the image-resolution effect below instead of `assetsData` identity, so
  // status-only refetches (approve, save, validate) don't re-resolve every image.
  const assetsSignature = useMemo(() => assetsContentSignature(allAssets), [allAssets]);

  // Blob object URLs, cached by asset identity and owned by the COMPONENT rather
  // than by a single run of the resolution effect.
  //
  // These URLs are handed downstream as the `previewUrl` of existing cards, and
  // those cards live in child state that only re-seeds on remount. So a URL must
  // stay valid for as long as its asset is on screen — revoking on effect cleanup
  // meant any refetch (approve!) invalidated URLs the children were still
  // rendering, which showed up as blank thumbnails until a manual refresh.
  //
  // Keyed by id + storage_path so replacing an asset's file re-resolves it.
  // Entries are pruned when their asset disappears, and all are revoked on unmount.
  const urlCacheRef = useRef<Map<string, string>>(new Map());
  const assetCacheKey = (a: CampaignAsset) => `${a.id}:${a.storage_path ?? ''}`;

  useEffect(() => {
    const cache = urlCacheRef.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  // Restore persisted validation_result from the DB on initial load so errors/warnings
  // survive a page refresh without requiring the user to re-validate.
  useEffect(() => {
    if (!assetsData) return;
    // Don't overwrite if a fresh validation has already been run this session
    if (fieldIssuesProp && Object.keys(fieldIssuesProp).length > 0) return;

    const variantAssets = allAssets.filter((a) => (a.variant_number ?? 1) === selectedVariant);
    const stored = variantAssets.find((a) => a.validation_result)?.validation_result;
    if (!stored) return;

    const indexAll = mockupType === 'meta-dynamic-creative'; // DC indexes primary_text, headline, description
    const indexPrimaryOnly = mockupType === 'meta'; // Meta Single only indexes primary_text
    const indexCarouselCards = mockupType === 'meta-carousel' || mockupType === 'linkedin-carousel';
    const normalizeField = (field: string): string => {
      // "ratio_items.feed" is a backend pseudo-ratio meaning "1:1 or 4:5" — either
      // one satisfies the feed placement. It is deliberately NOT rewritten to a
      // per-slot key: it describes the pair, so both feed slots subscribe to it
      // directly (see AssetSlot's fieldKey array). Rewriting it to
      // "ratio_items.1x1" used to leave the 4:5 slot looking valid.
      if (indexAll) {
        const pm = field.match(/^post_copies\[(\d+)\]/);
        if (pm) return `primary_text.${pm[1]}`;
        const hm = field.match(/^headlines\[(\d+)\]/);
        if (hm) return `headline.${hm[1]}`;
        const dm = field.match(/^descriptions\[(\d+)\]/);
        if (dm) return `description.${dm[1]}`;
        if (field === 'primary_text') return 'primary_text.0';
        return field;
      }
      if (indexPrimaryOnly) {
        const pm = field.match(/^post_copies\[(\d+)\]/);
        if (pm) return `primary_text.${pm[1]}`;
        if (field === 'primary_text') return 'primary_text.0';
      }
      if (indexCarouselCards) {
        const hm = field.match(/^headlines\[(\d+)\]/);
        if (hm) return `headline.${hm[1]}`;
        const dm = field.match(/^descriptions\[(\d+)\]/);
        if (dm) return `description.${dm[1]}`;
      }
      if (/^post_copies\[/.test(field)) return 'primary_text';
      if (/^headlines\[/.test(field)) return 'headline';
      if (/^descriptions\[/.test(field)) return 'description';
      // Per-card indexed fields: carousel_items[0].headline → headline.0
      const ch = field.match(/^carousel_items\[(\d+)\]\.headline/);
      if (ch) return `headline.${ch[1]}`;
      const cd = field.match(/^carousel_items\[(\d+)\]\.description/);
      if (cd) return `description.${cd[1]}`;
      // Per-card missing image → routed to that card's image slot
      const cf = field.match(/^carousel_items\[(\d+)\]\.hasFile/);
      if (cf) return `card_image.${cf[1]}`;
      // Legacy non-indexed fallback
      if (field === 'carousel_items.headline') return 'headline';
      if (field === 'carousel_items.description') return 'description';
      return field;
    };
    const issues: Record<string, FieldIssue> = {};
    const addToField = (
      field: string,
      type: 'error' | 'warning',
      message: string,
      rule?: string,
      originalField?: string,
    ) => {
      if (!issues[field]) issues[field] = { errors: [], warnings: [], warningMeta: [] };
      if (type === 'error') {
        issues[field].errors.push(message);
      } else {
        const idx = issues[field].warnings.length;
        issues[field].warnings.push(message);
        if (rule) {
          issues[field].warningMeta!.push({
            rule,
            field: originalField ?? field,
            key: `${rule}::${originalField ?? field}::${idx}`,
          });
        }
      }
    };
    for (const e of stored.errors ?? []) addToField(normalizeField(e.field), 'error', e.message);
    for (const w of stored.warnings ?? [])
      addToField(normalizeField(w.field), 'warning', w.message, w.rule, w.field);

    // Initialize AI feedback from stored validation result
    if (stored.feedback) {
      setAiFeedback(stored.feedback);
    }

    if (Object.keys(issues).length > 0) {
      const hasErrors = Object.values(issues).some((fi) => fi.errors.length > 0);
      const hasWarnings = Object.values(issues).some((fi) => fi.warnings.length > 0);
      onValidationResult?.(issues);
      onValidationComplete?.(hasErrors, hasWarnings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsData, selectedVariant]);

  // Report hasValidated, isApproved, anyVariantHasErrors across ALL variants.
  useCrossVariantStatus(
    allAssets,
    { onHasValidatedChange, onIsApprovedChange, onAnyVariantHasErrorsChange },
    { unsavedNewVariants, defaultVariantNumber: 1, requireStoragePath: true },
  );

  // Derive variant list from assets; fall back to [1] if none
  const apiVariantNumbers = useMemo(
    () => [...new Set(allAssets.map((a) => a.variant_number ?? 1))].sort((a, b) => a - b),
    [allAssets],
  );
  const derivedVariants: VariantState[] = useMemo(
    () =>
      apiVariantNumbers.length > 0
        ? apiVariantNumbers.map((n) => ({ number: n, saved: true }))
        : [{ number: 1, saved: true }],
    [apiVariantNumbers],
  );

  // Merge API-derived variants with any locally-added unsaved ones.
  // Once a new variant is saved and invalidateQueries fires, it appears in derivedVariants
  // and the filter drops it from the unsaved list automatically.
  const activeVariants: VariantState[] = useMemo(
    () => [
      ...derivedVariants,
      ...unsavedNewVariants
        .filter((n) => !apiVariantNumbers.includes(n))
        .map((n) => ({ number: n, saved: false })),
    ],
    [derivedVariants, unsavedNewVariants, apiVariantNumbers],
  );

  // Map of filename → variant numbers for cross-variant "used in" badges in GX modal
  const usedInVariants = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const asset of allAssets) {
      if (!asset.asset_name || !asset.storage_path) continue;
      const vn = asset.variant_number ?? 1;
      const existing = map.get(asset.asset_name);
      if (existing) {
        if (!existing.includes(vn)) existing.push(vn);
      } else {
        map.set(asset.asset_name, [vn]);
      }
    }
    return map;
  }, [allAssets]);

  // Gear Exchange data — fetched once and cached across variant switches.
  // Only re-fetches when fordshareIds change (i.e. user adds/changes FordShare IDs).
  const fordshareIds = useMemo(
    () => campaign?.fordshare_entries?.map((e) => e.id) ?? null,
    [campaign?.fordshare_entries],
  );
  const { gxData, gxLoading, gxError } = useGearExchangeSearch(
    campaignId,
    campaign?.fordshare_entries,
  );

  // Per-variant validation status computed from allAssets. Used for tab icons.
  const variantValidationStatus = useVariantValidationStatus(
    allAssets,
    activeVariants.map((v) => v.number),
  );

  // Assets for the currently selected variant
  function assetsForVariant(num: number): CampaignAsset[] {
    return allAssets.filter((a) => (a.variant_number ?? 1) === num);
  }

  // Resolved image URLs for the currently selected variant.
  // These are either GCS signed URLs (prod) or blob: object URLs created from an
  // authenticated proxy fetch (local dev — see resolveUrl below). Either way they
  // can be dropped straight into <img src> without any auth headers.
  const [apiImageUrl1x1, setApiImageUrl1x1] = useState<string | undefined>(undefined);
  const [apiImageUrl4x5, setApiImageUrl4x5] = useState<string | undefined>(undefined);
  const [apiImageUrl9x16, setApiImageUrl9x16] = useState<string | undefined>(undefined);
  // Tracks whether the async image-resolution pass has completed for the current variant.
  // Used to gate the loading skeleton — stays false until resolveUrl settles, so we never
  // show the empty mockup mid-load and then snap to the real image.
  const [imagesResolved, setImagesResolved] = useState(false);

  // True while we're waiting for the assets API or while blob URLs are being resolved.
  // We show shimmer skeletons during this window so the UI never flashes an empty
  // mockup or bare upload zones before the real content is ready.
  const variantHasAssets = allAssets.some((a) => (a.variant_number ?? 1) === selectedVariant);
  const showSkeleton = !!campaignId && (!assetsData || (variantHasAssets && !imagesResolved));

  useEffect(() => {
    const va = assetsForVariant(selectedVariant);
    // exactRatio, NOT normalizeRatio: normalizeRatio collapses 4x5 into the '1x1'
    // feed bucket, so with both slots populated `find` would return whichever row
    // came first and hydrate the wrong file into the 1:1 slot (and, on save,
    // overwrite it). Legacy 4:5 assets are stored as ratio='1x1' and therefore
    // still resolve to the 1:1 slot — deliberately, so old campaigns are unmoved.
    const a1x1 =
      va.find((a) => exactRatio(a.ratio) === '1x1') ?? va.find((a) => !a.ratio && !!a.storage_path);
    const a4x5 = va.find((a) => exactRatio(a.ratio) === '4x5');
    const a9x16 = va.find((a) => normalizeRatio(a.ratio) === '9x16');

    // Don't resolve images until asset data has loaded — prevents premature
    // empty resolutions that can cause cascading remounts and stale card state.
    if (!assetsData) return;

    // Reset resolved flag and clear stale image/card state so the previous
    // variant's data doesn't bleed into the new variant while async resolution runs.
    //
    // Clearing the resolved card lists is ONLY safe on an actual variant switch.
    // On a refetch of the SAME variant (save, approve, validate) the lists get
    // re-resolved to equivalent content, so blanking them first published a
    // transient "no assets" state to the card components — which seeded from it
    // and dropped everything on screen until a manual refresh. Validate is the
    // usual trigger: it saves first, which mints new asset ids, which changes
    // assetsSignature and re-runs this effect.
    setImagesResolved(false);
    setApiImageUrl1x1(undefined);
    setApiImageUrl4x5(undefined);
    setApiImageUrl9x16(undefined);
    if (prevVariantRef.current !== selectedVariant) {
      setMetaCarouselFeedResolvedCards([]);
      setMetaCarouselStoryResolvedCards([]);
      setSnapchatStoryResolvedCards([]);
      setLinkedInCarouselResolvedCards([]);
      setMetaDCInitial1x1([]);
      setMetaDCInitial9x16([]);
      setFlexibleInitialCards([]);
      setCollectionInitialProducts([]);
    }

    // Seed parent-level copy state synchronously so mockup updates immediately.
    // For flexible ad, assets have no ratio, so we fall back to va[0] (may be a placeholder
    // with no storage_path but with saved metadata like CTA/URL).
    const primary =
      a1x1 ?? a4x5 ?? a9x16 ?? (mockupType === 'meta-flexible-ad' ? va[0] : undefined);
    if (primary) {
      const ctaArr = normalizeCta(primary.cta);
      const firstCta = ctaArr[0] ?? 'Learn More';
      // Seed all format states from API so they're always up to date regardless of active format
      setMetaCaption(primary.post_copies?.[0] ?? '');
      setMetaHeadline(primary.headlines?.[0] ?? '');
      setMetaDescription(primary.descriptions?.[0] ?? '');
      setMetaCta(firstCta);
      setMetaUrl(primary.url ?? '');
      setMetaLegal(primary.legal ?? '');
      setTikTokCaption(primary.post_copies?.[0] ?? '');
      setTikTokCta(firstCta);
      setTikTokUrl(primary.url ?? '');
      setTikTokLegal(primary.legal ?? '');
      setSnapchatHeadline(primary.headlines?.[0] ?? '');
      setSnapchatUrl(primary.url ?? '');
      setSnapchatLegal(primary.legal ?? '');
      setSnapchatStoryHeadline(primary.headlines?.[0] ?? '');
      setSnapchatStoryUrl(primary.url ?? '');
      setSnapchatStoryLegal(primary.legal ?? '');
      setLinkedInCaption(primary.post_copies?.[0] ?? '');
      setLinkedInHeadline(primary.headlines?.[0] ?? '');
      setLinkedInCta(firstCta);
      setLinkedInUrl(primary.url ?? '');
      setLinkedInLegal(primary.legal ?? '');
      setLinkedInDocCaption(primary.post_copies?.[0] ?? '');
      setLinkedInDocHeadline(primary.headlines?.[0] ?? '');
      setLinkedInDocUrl(primary.url ?? '');
      setLinkedInDocLegal(primary.legal ?? '');
      setLinkedInCarouselCaption(primary.post_copies?.[0] ?? '');
      setLinkedInCarouselLegal(primary.legal ?? '');
      setMetaCarouselCaption(primary.post_copies?.[0] ?? '');
      setMetaCarouselDestinationUrl(primary.url ?? '');
      setMetaCarouselCta(firstCta);
      setMetaCarouselLegal(primary.legal ?? '');
      setCollectionCaption(primary.post_copies?.[0] ?? '');
      setCollectionCta(ctaArr[0] ?? 'Shop Now');
      setCollectionUrl(primary.url ?? '');
      setCollectionLegal(primary.legal ?? '');
      // DC text arrays — restored from DB on page load
      setMetaDCPrimaryTexts(primary.post_copies?.filter(Boolean) ?? ['']);
      setMetaDCHeadlines(primary.headlines?.filter(Boolean) ?? ['']);
      setMetaDCDescriptions(primary.descriptions?.filter(Boolean) ?? ['']);
      setMetaDCCtas(ctaArr.length ? ctaArr : ['Learn More']);
      setMetaDCCta(firstCta);
      setMetaDCUrl(primary.url ?? '');
      setMetaDCLegal(primary.legal ?? '');
      // Flexible Ad shared fields
      setFlexibleCta(firstCta);
      setFlexibleUrl(primary.url ?? '');
      setFlexibleLegal(primary.legal ?? '');
    } else if (prevVariantRef.current !== selectedVariant) {
      // New empty variant — reset all form state to defaults so stale data
      // from the previous variant doesn't bleed into the blank form.
      // Only runs on actual variant switch, NOT on assetsData refetch.
      setMetaCaption('');
      setMetaHeadline('');
      setMetaDescription('');
      setMetaCta('Learn More');
      setMetaUrl('');
      setMetaLegal('');
      setTikTokCaption('');
      setTikTokCta('Learn More');
      setTikTokUrl('');
      setTikTokLegal('');
      setSnapchatHeadline('');
      setSnapchatUrl('');
      setSnapchatLegal('');
      setSnapchatStoryHeadline('');
      setSnapchatStoryUrl('');
      setSnapchatStoryLegal('');
      setLinkedInCaption('');
      setLinkedInHeadline('');
      setLinkedInCta('Learn More');
      setLinkedInUrl('');
      setLinkedInLegal('');
      setLinkedInDocCaption('');
      setLinkedInDocHeadline('');
      setLinkedInDocUrl('');
      setLinkedInDocLegal('');
      setLinkedInCarouselCaption('');
      setLinkedInCarouselLegal('');
      setMetaCarouselCaption('');
      setMetaCarouselDestinationUrl('');
      setMetaCarouselCta('Learn More');
      setMetaCarouselLegal('');
      setCollectionCaption('');
      setCollectionCta('Shop Now');
      setCollectionUrl('');
      setCollectionLegal('');
      setMetaDCPrimaryTexts(['']);
      setMetaDCHeadlines(['']);
      setMetaDCDescriptions(['']);
      setMetaDCCta('Learn More');
      setMetaDCCtas(['Learn More']);
      setMetaDCUrl('');
      setMetaDCLegal('');
      setFlexibleCta('Learn More');
      setFlexibleUrl('');
      setFlexibleLegal('');
    }

    // Clear pending file uploads and deletions when switching variants so
    // files queued for one variant don't get saved under another.
    if (prevVariantRef.current !== selectedVariant) {
      setFile1x1(null);
      setFile4x5(null);
      setFile9x16(null);
      setPendingDeleteAssetIds([]);
      setMetaCarouselFeedNewFiles([]);
      setMetaCarouselFeed4x5NewFiles([]);
      setMetaCarouselFeedDeletedIds([]);
      setMetaCarouselStoryNewFiles([]);
      setMetaCarouselStoryDeletedIds([]);
      setMetaDCNewFiles1x1([]);
      setMetaDCNewFiles9x16([]);
      setMetaDCDeletedIds1x1([]);
      setMetaDCDeletedIds9x16([]);
      setFlexibleDeletedIds([]);
      setCollectionProductFiles([]);
      setCollectionProductDeletedIds([]);
      setSnapchatStoryNewFiles([]);
      setSnapchatStoryDeletedIds([]);
      setLinkedInFile(null);
      setLinkedInFile4x5(null);
      setLinkedInDocFile(null);
      setLinkedInCarouselNewFiles([]);
      setLinkedInCarousel4x5NewFiles([]);
      setLinkedInCarouselDeletedIds([]);
    }

    prevVariantRef.current = selectedVariant;

    // Resolve a loadable URL for each asset.
    //
    // In production, the backend generates a GCS signed URL so we can return it directly
    // and the browser loads it with no auth — fast and zero overhead.
    //
    // In local dev, GCS signed URL generation fails (the developer's ADC credentials need
    // the serviceAccountTokenCreator IAM role which isn't always set up). In that case
    // signed_url is null and we fall back to the backend proxy endpoint:
    //   GET /api/campaigns/:id/assets/:assetId/content
    // which streams the raw GCS blob. The catch: <img src> is a plain browser request
    // with no Authorization header, so it returns 401. To work around this, we download
    // the blob via authenticatedFetch (which injects the Okta Bearer token), then hand
    // the browser a blob: object URL it can render freely. Blob URLs are revoked on
    // effect cleanup to avoid memory leaks.
    let cancelled = false;

    const resolveUrl = async (asset: CampaignAsset | undefined): Promise<string | undefined> => {
      if (!asset) return undefined;
      if (asset.signed_url && !asset.signed_url.startsWith('file://')) return asset.signed_url;
      if (asset.storage_path && !asset.storage_path.startsWith('file://')) {
        // Reuse the blob URL already minted for this asset. Re-downloading would
        // produce a different URL string for identical bytes, needlessly changing
        // the `previewUrl` handed to children.
        const cached = urlCacheRef.current.get(assetCacheKey(asset));
        if (cached) return cached;
        const proxyUrl = `/api/campaigns/${campaignId}/assets/${asset.id}/content`;
        // For videos, avoid downloading the entire file into memory as a blob.
        // When auth is disabled (local dev), use the proxy URL directly so the
        // browser streams natively. When auth is enabled (prod), signed_url should
        // have been set above — but if it wasn't, fall through to blob download.
        const isVideo = asset.content_type?.startsWith('video/');
        const authDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true';
        if (isVideo && authDisabled) {
          return proxyUrl;
        }
        try {
          const resp = await authenticatedFetch(proxyUrl);
          if (resp.ok) {
            const blob = await resp.blob();
            // Use asset.content_type (or blob.type) so MediaDisplay can detect video.
            // createMediaBlobUrl registers the MIME type in blobMediaTypes for isBlobVideoUrl.
            const mimeType = asset.content_type || blob.type || 'image/jpeg';
            const objectUrl = createMediaBlobUrl(new File([blob], 'asset', { type: mimeType }));
            urlCacheRef.current.set(assetCacheKey(asset), objectUrl);
            return objectUrl;
          }
        } catch {
          /* silent */
        }
      }
      return undefined;
    };

    if (mockupType === 'snapchat-story') {
      // Resolve ALL 9x16 assets as story cards (each card is a separate CampaignAsset row)
      const storyAssets = va.filter((a) => normalizeRatio(a.ratio) === '9x16' && !!a.storage_path);
      Promise.all(
        storyAssets.map((a) =>
          resolveUrl(a).then((url) =>
            url
              ? {
                  imageUrl: url,
                  contentType: a.content_type,
                  existingAssetId: a.id,
                }
              : null,
          ),
        ),
      ).then((results) => {
        if (cancelled) return;
        setSnapchatStoryResolvedCards(
          results.filter(Boolean) as {
            imageUrl: string;
            contentType?: string | null;
            existingAssetId: string;
          }[],
        );
        setImagesResolved(true);
      });
    } else if (mockupType === 'meta-carousel') {
      // Resolve the feed crops (1:1 and 4:5) and the story cards (9:16) separately.
      // exactRatio, not normalizeRatio: normalizeRatio buckets 4x5 into '1x1', which
      // would interleave the two crops into one list. Legacy 4:5 files saved through
      // the old workaround carry ratio='1x1' and so stay in the 1:1 lane — correct,
      // that is the slot they are shown in.
      const feedAssets = va
        .filter((a) => exactRatio(a.ratio) === '1x1' && !!a.storage_path)
        .sort(byDisplayOrder);
      const feed4x5Assets = va
        .filter((a) => exactRatio(a.ratio) === '4x5' && !!a.storage_path)
        .sort(byDisplayOrder);
      const storyAssets = va
        .filter((a) => normalizeRatio(a.ratio) === '9x16' && !!a.storage_path)
        .sort(byDisplayOrder);
      // Per-card metadata stored on the primary asset: headlines[] = per-card headline, descriptions[] = per-card description
      // Shared fields: url = destination URL, cta = CTA (one for all cards)
      const primaryFeed = va.find((a) => normalizeRatio(a.ratio) === '1x1') ?? va[0];
      const savedFeedHeadlines = primaryFeed?.headlines ?? [];
      const savedFeedDescriptions = primaryFeed?.descriptions ?? [];
      const resolveCards = (assets: typeof feedAssets) =>
        Promise.all(
          assets.map((a) =>
            resolveUrl(a).then((url) =>
              url
                ? {
                    imageUrl: url,
                    existingAssetId: a.id,
                    contentType: a.content_type,
                    position: a.position ?? null,
                  }
                : null,
            ),
          ),
        );
      Promise.all([
        resolveCards(feedAssets),
        resolveCards(feed4x5Assets),
        resolveCards(storyAssets),
      ]).then(([feedResults, feed4x5Results, storyResults]) => {
        if (cancelled) return;
        // Build feed cards from the headline/description arrays (source of truth for card count).
        // Keep positional alignment — don't filter(Boolean) to avoid index shift when a
        // resolveUrl returns null (e.g. transient fetch/auth failure). See byCardPosition
        // for why the two crops are paired by `position` rather than array index.
        const feedByCard = byCardPosition(feedResults);
        const feed4x5ByCard = byCardPosition(feed4x5Results);
        const cardCount = Math.max(
          savedFeedHeadlines.length,
          savedFeedDescriptions.length,
          feedByCard.length,
          feed4x5ByCard.length,
        );
        const feedCards: MetaCarouselInitialItem[] = Array.from({ length: cardCount }, (_, i) => ({
          imageUrl: feedByCard[i]?.imageUrl,
          existingAssetId: feedByCard[i]?.existingAssetId,
          contentType: feedByCard[i]?.contentType,
          imageUrl4x5: feed4x5ByCard[i]?.imageUrl,
          existingAssetId4x5: feed4x5ByCard[i]?.existingAssetId,
          contentType4x5: feed4x5ByCard[i]?.contentType,
          headline: savedFeedHeadlines[i] ?? '',
          description: savedFeedDescriptions[i] ?? '',
        }));
        setMetaCarouselFeedResolvedCards(feedCards);
        setMetaCarouselStoryResolvedCards(
          storyResults.filter(Boolean) as typeof metaCarouselStoryResolvedCards,
        );
        setImagesResolved(true);
      });
    } else if (mockupType === 'meta-collection') {
      // All 1x1 assets sorted by created_at: first = hero cover, rest = product images.
      // One cover image is shared across both feed and story placements (CSS crops to fit).
      const all1x1 = va
        .filter((a) => normalizeRatio(a.ratio) === '1x1' && !!a.storage_path)
        .sort(byDisplayOrder);
      const heroAsset = all1x1[0];
      const productAssets = all1x1.slice(1, 4);
      // Product titles are stored in headlines[] on the primary asset
      const savedTitles: string[] = heroAsset?.headlines ?? [];
      Promise.all([
        resolveUrl(heroAsset),
        Promise.all(
          productAssets.map((a, i) =>
            resolveUrl(a).then((url) =>
              url
                ? {
                    imageUrl: url,
                    existingAssetId: a.id,
                    title: savedTitles[i] ?? '',
                  }
                : null,
            ),
          ),
        ),
      ]).then(([heroUrl, productResults]) => {
        if (cancelled) return;
        setApiImageUrl1x1(heroUrl);
        // Always create 3 product entries so saved titles (from heroAsset.headlines) are
        // restored even when no product image assets exist in the DB.
        const resolvedWithImages = productResults.filter(Boolean) as {
          imageUrl: string;
          existingAssetId: string;
          title: string;
        }[];
        const initialProds = Array.from({ length: 3 }, (_, i) => ({
          imageUrl: resolvedWithImages[i]?.imageUrl,
          existingAssetId: resolvedWithImages[i]?.existingAssetId,
          title: savedTitles[i] ?? resolvedWithImages[i]?.title ?? '',
        }));
        setCollectionInitialProducts(initialProds);
        setImagesResolved(true);
      });
    } else if (mockupType === 'meta-dynamic-creative') {
      // Each asset is a separate grid image — resolve all and store as DCItem[].
      //
      // exactRatio, not normalizeRatio: normalizeRatio folds 4:5 into '1x1', which
      // would interleave the two feed pools into one grid. Legacy 4:5 files saved
      // through the old workaround carry ratio='1x1' and so stay in the 1:1
      // section — correct, that is the section they were uploaded into.
      const resolveLane = (want: UploadRatio) =>
        Promise.all(
          va
            .filter((a) => exactRatio(a.ratio) === want && !!a.storage_path)
            .sort(byDisplayOrder)
            .map((a) =>
              resolveUrl(a).then((url) =>
                url
                  ? {
                      kind: 'existing' as const,
                      url,
                      existingAssetId: a.id,
                      contentType: a.content_type,
                    }
                  : null,
              ),
            ),
        );
      Promise.all([resolveLane('1x1'), resolveLane('4x5'), resolveLane('9x16')]).then(
        ([results1x1, results4x5, results9x16]) => {
          if (cancelled) return;
          const items1x1 = results1x1.filter(Boolean) as DCItem[];
          const items4x5 = results4x5.filter(Boolean) as DCItem[];
          const items9x16 = results9x16.filter(Boolean) as DCItem[];
          setMetaDCInitial1x1(items1x1);
          setMetaDCInitial4x5(items4x5);
          setMetaDCInitial9x16(items9x16);
          // Seed active mockup URLs from the first item of each ratio (all resolved
          // items are 'existing'). The feed mockup prefers 1:1 and falls back to 4:5,
          // so a campaign with only 4:5 assets still previews.
          const firstFeed = items1x1[0] ?? items4x5[0];
          const first9x16 = items9x16[0];
          if (firstFeed && firstFeed.kind === 'existing') setMetaDCImageUrl1x1(firstFeed.url);
          if (first9x16 && first9x16.kind === 'existing') setMetaDCImageUrl9x16(first9x16.url);
          setImagesResolved(true);
        },
      );
    } else if (mockupType === 'meta-flexible-ad') {
      // Each flexible asset is a self-contained creative card, in persisted display order
      const flexibleAssets = va.filter((a) => !!a.storage_path).sort(byDisplayOrder);
      const firstAsset = flexibleAssets[0];
      const savedPrimaries: string[] = firstAsset?.post_copies ?? [];
      const savedHeadlines: string[] = firstAsset?.headlines ?? [];
      const savedDescriptions: string[] = firstAsset?.descriptions ?? [];
      Promise.all(
        flexibleAssets.map((a, i) =>
          resolveUrl(a).then((url) =>
            url
              ? ({
                  existingAssetId: a.id,
                  imageUrl: url,
                  contentType: a.content_type,
                  primaryText: savedPrimaries[i] ?? '',
                  headline: savedHeadlines[i] ?? '',
                  description: savedDescriptions[i] ?? '',
                } satisfies FlexibleInitialCard)
              : null,
          ),
        ),
      ).then((results) => {
        if (cancelled) return;
        const cards = results.filter(Boolean) as FlexibleInitialCard[];
        setFlexibleInitialCards(cards);
        if (cards[0]) {
          setFlexibleImageUrl(cards[0].imageUrl);
          setFlexibleCaption(cards[0].primaryText);
          setFlexibleHeadline(cards[0].headline);
          setFlexibleDescription(cards[0].description);
        }
        setImagesResolved(true);
      });
    } else if (mockupType === 'linkedin-carousel') {
      // Resolve the two feed crops separately, as on the Meta carousel. exactRatio,
      // not normalizeRatio: normalizeRatio buckets 4x5 into '1x1', which would
      // interleave the crops into one list. Legacy 4:5 files saved through the old
      // workaround carry ratio='1x1' and so stay in the 1:1 slot — correct, that is
      // where they are shown.
      const carouselAssets = va
        .filter((a) => exactRatio(a.ratio) === '1x1' && !!a.storage_path)
        .sort(byDisplayOrder);
      const carousel4x5Assets = va
        .filter((a) => exactRatio(a.ratio) === '4x5' && !!a.storage_path)
        .sort(byDisplayOrder);
      // All assets share the same headlines/descriptions arrays — read from the first available asset
      const primaryCarousel = va.find((a) => normalizeRatio(a.ratio) === '1x1') ?? va[0];
      const savedHeadlines: string[] = primaryCarousel?.headlines ?? [];
      const savedDestUrls: string[] = primaryCarousel?.descriptions ?? [];
      if (
        carouselAssets.length === 0 &&
        carousel4x5Assets.length === 0 &&
        savedHeadlines.length > 0
      ) {
        // Text-only save: no images but per-card text was saved on the placeholder.
        // Create text-only initial items so the card data is restored.
        if (!cancelled) {
          setLinkedInCarouselResolvedCards(
            savedHeadlines.map((h, i) => ({
              headline: h,
              destinationUrl: savedDestUrls[i] ?? '',
            })),
          );
          setImagesResolved(true);
        }
      } else {
        const resolveCards = (assets: typeof carouselAssets) =>
          Promise.all(
            assets.map((a) =>
              resolveUrl(a).then((url) =>
                url ? { imageUrl: url, existingAssetId: a.id, position: a.position ?? null } : null,
              ),
            ),
          );
        Promise.all([resolveCards(carouselAssets), resolveCards(carousel4x5Assets)]).then(
          ([results, results4x5]) => {
            if (cancelled) return;
            // Pair the crops by stored `position` (the card index) rather than array
            // index — see byCardPosition.
            const byCard = byCardPosition(results);
            const by4x5Card = byCardPosition(results4x5);
            const cardCount = Math.max(
              savedHeadlines.length,
              savedDestUrls.length,
              byCard.length,
              by4x5Card.length,
            );
            setLinkedInCarouselResolvedCards(
              Array.from({ length: cardCount }, (_, i) => ({
                imageUrl: byCard[i]?.imageUrl,
                existingAssetId: byCard[i]?.existingAssetId,
                imageUrl4x5: by4x5Card[i]?.imageUrl,
                existingAssetId4x5: by4x5Card[i]?.existingAssetId,
                headline: savedHeadlines[i] ?? '',
                destinationUrl: savedDestUrls[i] ?? '',
              })),
            );
            setImagesResolved(true);
          },
        );
      }
    } else {
      Promise.all([resolveUrl(a1x1), resolveUrl(a4x5), resolveUrl(a9x16)]).then(
        ([url1x1, url4x5, url9x16]) => {
          if (cancelled) return;
          setApiImageUrl1x1(url1x1);
          setApiImageUrl4x5(url4x5);
          setApiImageUrl9x16(url9x16);
          setImagesResolved(true);
        },
      );
    }

    return () => {
      cancelled = true;
    };
    // Depends on the CONTENT signature, not `assetsData` identity: status-only
    // refetches (approve, validate) leave every field this effect reads
    // untouched, so they no longer tear down and re-resolve every image.
    //
    // This is only safe because blob URLs are now owned by urlCacheRef, keyed by
    // asset identity, and outlive any single run of this effect. Previously the
    // cleanup revoked the URLs it had created — so a cleanup that ran without a
    // corresponding re-resolve (skipped as a no-op) left children rendering
    // revoked URLs. If you ever move blob ownership back in here, revert this dep
    // to `assetsData` at the same time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, assetsSignature]);

  // Drop cached blob URLs for assets that no longer exist (deleted, or replaced
  // with a new storage_path). Runs after resolution so URLs still in use are kept.
  useEffect(() => {
    const live = new Set(allAssets.map(assetCacheKey));
    urlCacheRef.current.forEach((url, key) => {
      if (!live.has(key)) {
        URL.revokeObjectURL(url);
        urlCacheRef.current.delete(key);
      }
    });
  }, [allAssets]);

  // The form key drives AdDetailsContent remounts — must match the key prop below.
  //
  // Keyed on WHICH assets are shown, not how many. Counts alone missed the case
  // that motivated this: deleting one card and adding another leaves the count
  // unchanged, so the form never remounted and the card components — which seed
  // from `initialItems` once — kept rendering the previous asset's data.
  const cardsKey = (cards: { existingAssetId?: string; imageUrl?: string }[]) =>
    cards.map((c) => c.existingAssetId ?? c.imageUrl ?? '∅').join(',');
  const dcKey = (items: DCItem[]) =>
    items.map((i) => (i.kind === 'existing' ? i.existingAssetId : i.file.name)).join(',');
  const formKey = [
    campaignId ?? 'local',
    selectedVariant,
    allAssets.length > 0,
    apiImageUrl1x1 ?? '',
    apiImageUrl4x5 ?? '',
    apiImageUrl9x16 ?? '',
    cardsKey(linkedInCarouselResolvedCards),
    cardsKey(metaCarouselFeedResolvedCards),
    cardsKey(metaCarouselStoryResolvedCards),
    cardsKey(collectionInitialProducts),
    dcKey(metaDCInitial1x1),
    dcKey(metaDCInitial4x5),
    dcKey(metaDCInitial9x16),
    cardsKey(flexibleInitialCards),
    cardsKey(snapchatStoryResolvedCards),
  ].join('-');

  // Keep loadedValues in sync with the API whenever assets or variant changes.
  // isDirty is derived by comparing current form state against this snapshot.
  useEffect(() => {
    const va = assetsForVariant(selectedVariant);
    const primary =
      va.find((a) => normalizeRatio(a.ratio) === '1x1') ??
      va.find((a) => normalizeRatio(a.ratio) === '9x16') ??
      va[0];
    if (primary) {
      const ctaArr = normalizeCta(primary.cta);
      // Offer data is per variant, so it comes off the same primary row and is
      // seeded into live state here — switching variants must not carry it over.
      const seededIsOfferLed = !!primary.is_offer_led;
      const seededOfferEndDate = primary.offer_end_date ?? '';
      setIsOfferLed(seededIsOfferLed);
      setOfferEndDate(seededOfferEndDate);
      setLoadedValues({
        postCopies: (primary.post_copies?.filter(Boolean) ?? []) as string[],
        headline: primary.headlines?.[0] ?? '',
        description: primary.descriptions?.[0] ?? '',
        cta: ctaArr[0] ?? 'Learn More',
        url: primary.url ?? '',
        legal: primary.legal ?? '',
        isOfferLed: seededIsOfferLed,
        offerEndDate: seededOfferEndDate,
        carouselHeadlines:
          mockupType === 'linkedin-carousel' || mockupType === 'meta-carousel'
            ? (primary.headlines ?? [])
            : undefined,
        // linkedin-carousel: descriptions[] = per-card dest URLs; meta-carousel: descriptions[] = per-card descriptions
        carouselDestUrls:
          mockupType === 'linkedin-carousel' || mockupType === 'meta-carousel'
            ? (primary.descriptions ?? [])
            : undefined,
        dcHeadlines:
          mockupType === 'meta-dynamic-creative' || mockupType === 'meta-flexible-ad'
            ? (primary.headlines ?? [])
            : undefined,
        dcDescriptions:
          mockupType === 'meta-dynamic-creative' || mockupType === 'meta-flexible-ad'
            ? (primary.descriptions ?? [])
            : undefined,
        dcCtas:
          mockupType === 'meta-dynamic-creative'
            ? ctaArr.length
              ? ctaArr
              : ['Learn More']
            : undefined,
      });
    } else {
      setIsOfferLed(false);
      setOfferEndDate('');
      setLoadedValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, assetsSignature]);

  // isDirty: true when any save-payload field differs from what was last loaded/saved.
  // Derived purely from state — no callbacks, no timing effects, always correct.
  const isDirty = useMemo(() => {
    if (file1x1 !== null || file4x5 !== null || file9x16 !== null) return true;
    if (pendingDeleteAssetIds.length > 0) return true;
    // Offer-led applies to every ad format, so it's compared once here rather
    // than repeated inside each per-mockup branch below.
    if (loadedValues) {
      if (isOfferLed !== loadedValues.isOfferLed || offerEndDate !== loadedValues.offerEndDate)
        return true;
    } else if (isOfferLed || offerEndDate) {
      return true;
    }
    if (mockupType === 'tiktok') {
      if (!loadedValues) {
        return !!tikTokCaption.trim() || !!tikTokUrl.trim() || !!tikTokLegal.trim();
      }
      return (
        tikTokCaption !== (loadedValues.postCopies[0] ?? '') ||
        tikTokCta !== loadedValues.cta ||
        tikTokUrl !== loadedValues.url ||
        tikTokLegal !== loadedValues.legal
      );
    }
    if (mockupType === 'snapchat') {
      if (!loadedValues) {
        return !!snapchatHeadline.trim() || !!snapchatUrl.trim() || !!snapchatLegal.trim();
      }
      return (
        snapchatHeadline !== (loadedValues.headline ?? '') ||
        snapchatUrl !== loadedValues.url ||
        snapchatLegal !== loadedValues.legal
      );
    }
    if (mockupType === 'snapchat-story') {
      if (snapchatStoryNewFiles.length > 0 || snapchatStoryDeletedIds.length > 0) return true;
      if (!loadedValues) {
        return (
          !!snapchatStoryHeadline.trim() || !!snapchatStoryUrl.trim() || !!snapchatStoryLegal.trim()
        );
      }
      return (
        snapchatStoryHeadline !== (loadedValues.headline ?? '') ||
        snapchatStoryUrl !== loadedValues.url ||
        snapchatStoryLegal !== loadedValues.legal
      );
    }
    if (mockupType === 'linkedin') {
      if (linkedInFile !== null) return true;
      if (linkedInFile4x5 !== null) return true;
      if (pendingDeleteAssetIds.length > 0) return true;
      if (!loadedValues) {
        return !!linkedInCaption.trim() || !!linkedInUrl.trim() || !!linkedInLegal.trim();
      }
      return (
        linkedInCaption !== (loadedValues.postCopies[0] ?? '') ||
        linkedInHeadline !== loadedValues.headline ||
        linkedInCta !== loadedValues.cta ||
        linkedInUrl !== loadedValues.url ||
        linkedInLegal !== loadedValues.legal
      );
    }
    if (mockupType === 'linkedin-document') {
      if (linkedInDocFile !== null) return true;
      if (!loadedValues) {
        return !!linkedInDocCaption.trim() || !!linkedInDocUrl.trim() || !!linkedInDocLegal.trim();
      }
      return (
        linkedInDocCaption !== (loadedValues.postCopies[0] ?? '') ||
        linkedInDocHeadline !== loadedValues.headline ||
        linkedInDocUrl !== loadedValues.url ||
        linkedInDocLegal !== loadedValues.legal
      );
    }
    if (mockupType === 'meta-carousel') {
      // Guard: during variant switch, CardSection remounts with default empty cards
      // before seeding from resolved items. Detect this transient state by checking
      // that cardData has fewer items than what we resolved from the DB (seeding
      // hasn't run yet), or that cardData is at the default MIN_CARDS=2 while
      // the DB has more cards. Don't treat this as dirty — it would trigger
      // spurious auto-saves and cause variant error flip-flopping.
      const MIN_META_CAROUSEL_CARDS = 2;
      const isMetaCarouselFeedSeedingState =
        metaCarouselFeedNewFiles.length === 0 &&
        metaCarouselFeedDeletedIds.length === 0 &&
        metaCarouselFeed4x5NewFiles.length === 0 &&
        metaCarouselStoryNewFiles.length === 0 &&
        metaCarouselStoryDeletedIds.length === 0 &&
        metaCarouselFeedResolvedCards.length > 0 &&
        (metaCarouselFeedCardData.length === 0 ||
          (metaCarouselFeedCardData.length === MIN_META_CAROUSEL_CARDS &&
            metaCarouselFeedResolvedCards.length > MIN_META_CAROUSEL_CARDS &&
            metaCarouselFeedCardData.every((c) => !c.headline.trim() && !c.description.trim())));
      if (isMetaCarouselFeedSeedingState) {
        return false;
      }
      if (
        metaCarouselFeedNewFiles.length > 0 ||
        metaCarouselFeed4x5NewFiles.length > 0 ||
        metaCarouselStoryNewFiles.length > 0
      )
        return true;
      if (metaCarouselFeedDeletedIds.length > 0 || metaCarouselStoryDeletedIds.length > 0)
        return true;
      if (!loadedValues) {
        return (
          !!metaCarouselCaption.trim() ||
          !!metaCarouselLegal.trim() ||
          !!metaCarouselDestinationUrl.trim() ||
          metaCarouselFeedCardData.some((c) => !!c.headline.trim() || !!c.description.trim())
        );
      }
      return (
        metaCarouselCaption !== (loadedValues.postCopies[0] ?? '') ||
        metaCarouselLegal !== loadedValues.legal ||
        metaCarouselDestinationUrl !== (loadedValues.url ?? '') ||
        metaCarouselCta !== (loadedValues.cta ?? 'Learn More') ||
        JSON.stringify(metaCarouselFeedCardData.map((c) => c.headline)) !==
          JSON.stringify(loadedValues.carouselHeadlines ?? []) ||
        JSON.stringify(metaCarouselFeedCardData.map((c) => c.description)) !==
          JSON.stringify(loadedValues.carouselDestUrls ?? [])
      );
    }
    if (mockupType === 'linkedin-carousel') {
      if (
        linkedInCarouselNewFiles.length > 0 ||
        linkedInCarousel4x5NewFiles.length > 0 ||
        linkedInCarouselDeletedIds.length > 0
      )
        return true;
      if (!loadedValues) {
        return (
          !!linkedInCarouselCaption.trim() ||
          linkedInCarouselCardData.some((c) => !!c.headline.trim())
        );
      }
      return (
        linkedInCarouselCaption !== (loadedValues.postCopies[0] ?? '') ||
        linkedInCarouselLegal !== loadedValues.legal ||
        JSON.stringify(linkedInCarouselCardData.map((c) => c.headline)) !==
          JSON.stringify(loadedValues.carouselHeadlines ?? []) ||
        JSON.stringify(linkedInCarouselCardData.map((c) => c.destinationUrl)) !==
          JSON.stringify(loadedValues.carouselDestUrls ?? [])
      );
    }
    if (mockupType === 'meta-dynamic-creative') {
      if (
        metaDCNewFiles1x1.length > 0 ||
        metaDCNewFiles4x5.length > 0 ||
        metaDCNewFiles9x16.length > 0
      )
        return true;
      if (
        metaDCDeletedIds1x1.length > 0 ||
        metaDCDeletedIds4x5.length > 0 ||
        metaDCDeletedIds9x16.length > 0
      )
        return true;
      if (!loadedValues) {
        return (
          metaDCPrimaryTexts.some((t) => t.trim() !== '') ||
          metaDCHeadlines.some((t) => t.trim() !== '') ||
          metaDCDescriptions.some((t) => t.trim() !== '') ||
          !!metaDCUrl.trim() ||
          !!metaDCLegal.trim()
        );
      }
      return (
        JSON.stringify(metaDCPrimaryTexts.filter(Boolean)) !==
          JSON.stringify(loadedValues.postCopies) ||
        JSON.stringify(metaDCHeadlines.filter(Boolean)) !==
          JSON.stringify((loadedValues.dcHeadlines ?? []).filter(Boolean)) ||
        JSON.stringify(metaDCDescriptions.filter(Boolean)) !==
          JSON.stringify((loadedValues.dcDescriptions ?? []).filter(Boolean)) ||
        JSON.stringify(metaDCCtas.filter(Boolean)) !==
          JSON.stringify((loadedValues.dcCtas ?? ['Learn More']).filter(Boolean)) ||
        metaDCUrl !== loadedValues.url ||
        metaDCLegal !== loadedValues.legal
      );
    }
    if (mockupType === 'meta-flexible-ad') {
      if (flexibleCards.some((c) => c.file !== null)) return true;
      if (flexibleDeletedIds.length > 0) return true;
      if (!loadedValues) {
        return (
          flexibleCards.some(
            (c) => c.primaryText.trim() || c.headline.trim() || c.description.trim(),
          ) ||
          !!flexibleUrl.trim() ||
          !!flexibleLegal.trim()
        );
      }
      return (
        JSON.stringify(flexibleCards.map((c) => c.primaryText).filter(Boolean)) !==
          JSON.stringify(loadedValues.postCopies ?? []) ||
        JSON.stringify(flexibleCards.map((c) => c.headline).filter(Boolean)) !==
          JSON.stringify((loadedValues.dcHeadlines ?? []).filter(Boolean)) ||
        JSON.stringify(flexibleCards.map((c) => c.description).filter(Boolean)) !==
          JSON.stringify((loadedValues.dcDescriptions ?? []).filter(Boolean)) ||
        flexibleCta !== (loadedValues.cta ?? 'Learn More') ||
        flexibleUrl !== (loadedValues.url ?? '') ||
        flexibleLegal !== (loadedValues.legal ?? '')
      );
    }
    if (mockupType === 'meta-collection') {
      if (collectionProductFiles.some(Boolean) || collectionProductDeletedIds.length > 0)
        return true;
      if (!loadedValues) {
        return (
          !!collectionCaption.trim() ||
          !!collectionUrl.trim() ||
          !!collectionLegal.trim() ||
          collectionProducts.some((p) => !!(p.title ?? '').trim())
        );
      }
      return (
        collectionCaption !== (loadedValues.postCopies[0] ?? '') ||
        collectionCta !== (loadedValues.cta ?? 'Shop Now') ||
        collectionUrl !== (loadedValues.url ?? '') ||
        collectionLegal !== (loadedValues.legal ?? '')
      );
    }
    // Default: Meta and all other formats
    if (!loadedValues) {
      return (
        metaAllPostCopies.some((t) => t.trim() !== '') ||
        !!metaUrl.trim() ||
        !!metaLegal.trim() ||
        !!metaHeadline.trim() ||
        !!metaDescription.trim()
      );
    }
    return (
      JSON.stringify(metaAllPostCopies.filter(Boolean)) !==
        JSON.stringify(loadedValues.postCopies) ||
      metaHeadline !== loadedValues.headline ||
      metaDescription !== loadedValues.description ||
      metaCta !== loadedValues.cta ||
      metaUrl !== loadedValues.url ||
      metaLegal !== loadedValues.legal
    );
  }, [
    mockupType,
    file1x1,
    file4x5,
    file9x16,
    pendingDeleteAssetIds,
    loadedValues,
    metaAllPostCopies,
    metaHeadline,
    metaDescription,
    metaCta,
    metaUrl,
    metaLegal,
    metaDCPrimaryTexts,
    metaDCHeadlines,
    metaDCDescriptions,
    metaDCCtas,
    metaDCUrl,
    metaDCLegal,
    metaDCNewFiles1x1,
    metaDCNewFiles4x5,
    metaDCNewFiles9x16,
    metaDCDeletedIds1x1,
    metaDCDeletedIds4x5,
    metaDCDeletedIds9x16,
    metaCarouselCaption,
    metaCarouselLegal,
    metaCarouselFeedNewFiles,
    metaCarouselFeed4x5NewFiles,
    metaCarouselFeedDeletedIds,
    metaCarouselFeedCardData,
    metaCarouselFeedResolvedCards,
    metaCarouselStoryNewFiles,
    metaCarouselStoryDeletedIds,
    metaCarouselCta,
    metaCarouselDestinationUrl,
    tikTokCaption,
    tikTokCta,
    tikTokUrl,
    tikTokLegal,
    snapchatHeadline,
    snapchatUrl,
    snapchatLegal,
    snapchatStoryHeadline,
    snapchatStoryUrl,
    snapchatStoryLegal,
    snapchatStoryNewFiles,
    snapchatStoryDeletedIds,
    linkedInFile,
    linkedInFile4x5,
    linkedInCaption,
    linkedInHeadline,
    linkedInCta,
    linkedInUrl,
    linkedInLegal,
    linkedInDocFile,
    linkedInDocCaption,
    linkedInDocHeadline,
    linkedInDocUrl,
    linkedInDocLegal,
    linkedInCarouselCaption,
    linkedInCarouselLegal,
    linkedInCarouselNewFiles,
    linkedInCarousel4x5NewFiles,
    linkedInCarouselDeletedIds,
    linkedInCarouselCardData,
    collectionCaption,
    collectionCta,
    collectionUrl,
    collectionLegal,
    collectionProducts,
    collectionProductFiles,
    collectionProductDeletedIds,
    flexibleCards,
    flexibleDeletedIds,
    flexibleCta,
    flexibleUrl,
    flexibleLegal,
    isOfferLed,
    offerEndDate,
  ]);

  // Bubble dirty state to parent (header Save button enable/disable)
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const imageUrl1x1 = useMemo(
    () => (file1x1 ? createMediaBlobUrl(file1x1) : apiImageUrl1x1),
    [file1x1, apiImageUrl1x1],
  );
  const imageUrl4x5 = useMemo(
    () => (file4x5 ? createMediaBlobUrl(file4x5) : apiImageUrl4x5),
    [file4x5, apiImageUrl4x5],
  );
  const imageUrl9x16 = useMemo(
    () => (file9x16 ? createMediaBlobUrl(file9x16) : apiImageUrl9x16),
    [file9x16, apiImageUrl9x16],
  );
  const linkedInImageUrl1x1 = useMemo(
    () => (linkedInFile ? createMediaBlobUrl(linkedInFile) : apiImageUrl1x1),
    [linkedInFile, apiImageUrl1x1],
  );
  const linkedInImageUrl4x5 = useMemo(
    () => (linkedInFile4x5 ? createMediaBlobUrl(linkedInFile4x5) : apiImageUrl4x5),
    [linkedInFile4x5, apiImageUrl4x5],
  );

  // Content type for the active 1x1 and 9x16 assets — used by MediaDisplay to detect
  // video reliably (blob URLs have no extension; signed URLs may omit it too).
  const contentType1x1 = useMemo(() => {
    if (file1x1) return file1x1.type;
    const va = assetsForVariant(selectedVariant);
    // exactRatio, not normalizeRatio: normalizeRatio buckets 4x5 into '1x1', so
    // with both slots filled this could return the 4:5 row's content_type and
    // mis-detect video in the 1:1 slot. Legacy 4:5 files stored as ratio='1x1'
    // still match, which is correct — they live in the 1:1 slot.
    return va.find((a) => exactRatio(a.ratio) === '1x1')?.content_type ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file1x1, assetsData, selectedVariant]);
  const contentType4x5 = useMemo(() => {
    if (file4x5) return file4x5.type;
    const va = assetsForVariant(selectedVariant);
    return va.find((a) => exactRatio(a.ratio) === '4x5')?.content_type ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file4x5, assetsData, selectedVariant]);

  // 1:1 and 4:5 share ONE feed mockup (the mockup is deliberately unchanged), so
  // only one of them can be on screen at a time. This picks which. The user drives
  // it by clicking an upload thumbnail; we fall back to whichever slot actually has
  // an asset so the mockup is never blank when only the other one is filled.
  const [feedPreviewSlot, setFeedPreviewSlot] = useState<'1x1' | '4x5'>('1x1');
  const activeFeedSlot: '1x1' | '4x5' =
    feedPreviewSlot === '4x5' && imageUrl4x5
      ? '4x5'
      : feedPreviewSlot === '1x1' && imageUrl1x1
        ? '1x1'
        : imageUrl1x1
          ? '1x1'
          : imageUrl4x5
            ? '4x5'
            : '1x1';
  const feedImageUrl = activeFeedSlot === '4x5' ? imageUrl4x5 : imageUrl1x1;
  const feedContentType = activeFeedSlot === '4x5' ? contentType4x5 : contentType1x1;

  // The carousel mockup takes one image per card, so the selected crop is flattened
  // here rather than teaching the mockup about slots. Per card, not per strip: a
  // card with only a 1:1 still shows it while its neighbour shows its 4:5, which
  // beats blanking the card because the chosen crop happens to be missing.
  const metaCarouselFeedMockupItems = useMemo(
    () =>
      metaCarouselFeedItems.map((it) => {
        const use4x5 = metaCarouselFeedSlot === '4x5' ? !!it.imageUrl4x5 : !it.imageUrl;
        return {
          imageUrl: use4x5 ? it.imageUrl4x5 : it.imageUrl,
          contentType: use4x5 ? it.contentType4x5 : it.contentType,
          headline: it.headline,
          description: it.description,
        };
      }),
    [metaCarouselFeedItems, metaCarouselFeedSlot],
  );

  // Same flattening for the LinkedIn carousel — its mockup also takes one image
  // per card.
  const linkedInCarouselMockupItems = useMemo(
    () =>
      linkedInCarouselItems.map((it) => ({
        imageUrl: (linkedInCarouselFeedSlot === '4x5' ? !!it.imageUrl4x5 : !it.imageUrl)
          ? it.imageUrl4x5
          : it.imageUrl,
        headline: it.headline,
      })),
    [linkedInCarouselItems, linkedInCarouselFeedSlot],
  );

  // LinkedIn Single has the same two-slot / one-mockup shape as Meta Single, so it
  // reuses `feedPreviewSlot` — only one format is ever mounted at a time. The
  // fallbacks are duplicated rather than shared because the URLs differ.
  const activeLinkedInSlot: '1x1' | '4x5' =
    feedPreviewSlot === '4x5' && linkedInImageUrl4x5
      ? '4x5'
      : feedPreviewSlot === '1x1' && linkedInImageUrl1x1
        ? '1x1'
        : linkedInImageUrl1x1
          ? '1x1'
          : linkedInImageUrl4x5
            ? '4x5'
            : '1x1';
  const linkedInImageUrl = activeLinkedInSlot === '4x5' ? linkedInImageUrl4x5 : linkedInImageUrl1x1;
  const contentType9x16 = useMemo(() => {
    if (file9x16) return file9x16.type;
    const va = assetsForVariant(selectedVariant);
    return va.find((a) => normalizeRatio(a.ratio) === '9x16')?.content_type ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file9x16, assetsData, selectedVariant]);

  // Swap in format-specific asset/text state for formats that manage their own selections
  const isDC = mockupType === 'meta-dynamic-creative';
  const isFlexible = mockupType === 'meta-flexible-ad';
  const effectiveImageUrl1x1 = isDC
    ? metaDCImageUrl1x1
    : isFlexible
      ? flexibleImageUrl
      : feedImageUrl;
  const effectiveImageUrl9x16 = isDC ? metaDCImageUrl9x16 : imageUrl9x16;
  const effectiveCaption = isDC ? metaDCCaption : isFlexible ? flexibleCaption : metaCaption;
  const effectiveHeadline = isDC ? metaDCHeadline : isFlexible ? flexibleHeadline : metaHeadline;
  const effectiveDescription = isDC
    ? metaDCDescription
    : isFlexible
      ? flexibleDescription
      : metaDescription;
  const effectiveCta = isDC ? metaDCCta : isFlexible ? flexibleCta : metaCta;

  const ratios = FORMAT_RATIOS[mockupType];
  const effectiveType = resolveEffectiveMockupType(mockupType, selectedRatio);
  const isStoryFormat = STORY_FORMAT_TYPES.includes(effectiveType);
  const isCollection = effectiveType === 'meta-collection';
  const isLinkedIn =
    effectiveType === 'linkedin' ||
    effectiveType === 'linkedin-document' ||
    effectiveType === 'linkedin-carousel';

  const multipleVariants = activeVariants.length > 1;

  async function switchVariant(num: number) {
    if (num === selectedVariant) return;
    if (isDirty) {
      try {
        await handleSave();
      } catch {
        // Save failed — toast already shown by handleSave, still switch
      }
    }
    setSelectedVariant(num);
    setMetaCarouselFeedResolvedCards([]);
    setMetaCarouselStoryResolvedCards([]);
    setSnapchatStoryResolvedCards([]);
    setLinkedInCarouselResolvedCards([]);
    setMetaDCInitial1x1([]);
    setMetaDCInitial9x16([]);
    setFlexibleInitialCards([]);
    setCollectionInitialProducts([]);
    setApiImageUrl1x1(undefined);
    setApiImageUrl4x5(undefined);
    setApiImageUrl9x16(undefined);
    onValidationResult?.({});
  }

  // Can add a new variant only if the currently selected variant is saved in the DB
  // (same logic as old modal: existingVariants.has(selectedVariant))
  const canAddVariant =
    activeVariants.length < MAX_VARIANTS && apiVariantNumbers.includes(selectedVariant);

  async function addVariant() {
    if (!canAddVariant) return;
    if (isDirty) {
      try {
        await handleSave();
      } catch {
        /* toast already shown */
      }
    }
    const used = new Set(activeVariants.map((v) => v.number));
    const next = Array.from({ length: MAX_VARIANTS }, (_, i) => i + 1).find((n) => !used.has(n));
    if (next === undefined) return; // all variant slots are in use
    setUnsavedNewVariants((prev) => [...prev, next]);
    setSelectedVariant(next);
    setMetaCarouselFeedResolvedCards([]);
    setMetaCarouselStoryResolvedCards([]);
    setSnapchatStoryResolvedCards([]);
    setLinkedInCarouselResolvedCards([]);
    setMetaDCInitial1x1([]);
    setMetaDCInitial9x16([]);
    setFlexibleInitialCards([]);
    setCollectionInitialProducts([]);
    setApiImageUrl1x1(undefined);
    setApiImageUrl4x5(undefined);
    setApiImageUrl9x16(undefined);
    onValidationResult?.({});
  }

  function requestRemoveVariant(v: VariantState) {
    if (!v.saved) {
      confirmRemoveVariant(v.number); // unsaved local variant — remove immediately
    } else {
      setPendingDelete(v.number);
    }
  }

  async function confirmRemoveVariant(num: number) {
    const isSaved = apiVariantNumbers.includes(num);
    if (isSaved && campaignId) {
      try {
        await deleteVariantApi(campaignId, num);
        // Await the refetch: the backend compacts variant numbers on delete, so
        // `activeVariants` below is stale until the assets come back. Picking a
        // survivor from the pre-delete numbering selects a variant that no
        // longer exists and renders an empty pane until a manual refresh.
        await queryClient.invalidateQueries({
          queryKey: ['campaign-assets', campaignId],
        });
      } catch {
        toast.error('Failed to delete variant');
        setPendingDelete(null);
        return;
      }
    }
    // Remove from unsaved list if it was a local-only variant
    setUnsavedNewVariants((prev) => prev.filter((n) => n !== num));

    // Mirror the backend's compaction: survivors above the deleted variant each
    // shift down one. Applies to the saved variants only — unsaved local ones
    // were never renumbered server-side.
    const renumber = (n: number) => (isSaved && n > num ? n - 1 : n);
    const remaining = activeVariants
      .filter((v) => v.number !== num)
      .map((v) => (v.saved ? renumber(v.number) : v.number))
      .sort((a, b) => a - b);

    if (selectedVariant === num) {
      setSelectedVariant(remaining[0] ?? 1);
      setMetaCarouselFeedResolvedCards([]);
      setMetaCarouselStoryResolvedCards([]);
      setSnapchatStoryResolvedCards([]);
      setLinkedInCarouselResolvedCards([]);
      setMetaDCInitial1x1([]);
      setMetaDCInitial9x16([]);
      setFlexibleInitialCards([]);
      setCollectionInitialProducts([]);
      setApiImageUrl1x1(undefined);
      setApiImageUrl4x5(undefined);
      setApiImageUrl9x16(undefined);
    } else if (selectedVariant > num) {
      // Still viewing the same variant, but it just shifted down a number.
      setSelectedVariant(renumber(selectedVariant));
    }
    setPendingDelete(null);
  }

  async function confirmDeleteAllAssets() {
    if (!campaignId) return;
    setIsDeletingAll(true);
    try {
      // Delete all uploaded assets (if any)
      if (allAssets.length > 0) {
        await deleteCampaignAssets(campaignId);
      }
      // Clear the asset_type_id so the user can pick a new format
      await updateCampaign(campaignId, { asset_type_id: null });
      await queryClient.invalidateQueries({
        queryKey: ['campaign-assets', campaignId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['campaign', campaignId],
      });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      if (allAssets.length > 0) {
        toast.success('All assets deleted');
      } else {
        toast.success('Format cleared');
      }
      setShowDeleteAllConfirm(false);
      // Tell the parent to clear local mockup state and show format selection
      onDeleteAll?.();
    } catch {
      toast.error('Failed to delete assets');
    } finally {
      setIsDeletingAll(false);
    }
  }

  const formatLabel =
    formatLabelProp ?? MOCKUP_OPTIONS.find((o) => o.value === mockupType)?.label ?? mockupType;

  // ── Format-aware save payload ─────────────────────────────────────────────
  // Returns the copy fields to POST based on the active format. Add a new case
  // here when wiring up each additional platform.
  function buildSavePayload() {
    switch (mockupType) {
      case 'tiktok':
        return {
          post_copies: tikTokCaption ? [tikTokCaption] : [],
          headlines: [] as string[],
          descriptions: [] as string[],
          cta: tikTokCta ? [tikTokCta] : undefined,
          url: tikTokUrl,
          legal: tikTokLegal,
        };
      case 'snapchat':
        return {
          post_copies: [] as string[],
          headlines: snapchatHeadline ? [snapchatHeadline] : [],
          descriptions: [] as string[],
          cta: undefined,
          url: snapchatUrl,
          legal: snapchatLegal,
        };
      case 'snapchat-story':
        return {
          post_copies: [] as string[],
          headlines: snapchatStoryHeadline ? [snapchatStoryHeadline] : [],
          descriptions: [] as string[],
          cta: undefined,
          url: snapchatStoryUrl,
          legal: snapchatStoryLegal,
          carousel_items: Array(snapchatStoryTotalCount)
            .fill(null)
            .map((_, i) => ({
              headline: snapchatStoryHeadline,
              description: '',
              hasFile: snapchatStoryHasFiles[i] ?? false,
            })),
        };
      case 'linkedin':
        return {
          post_copies: linkedInCaption ? [linkedInCaption] : [],
          headlines: linkedInHeadline ? [linkedInHeadline] : [],
          descriptions: [] as string[],
          cta: linkedInCta ? [linkedInCta] : undefined,
          url: linkedInUrl,
          legal: linkedInLegal,
          ratio: '1x1',
        };
      case 'linkedin-document':
        return {
          post_copies: linkedInDocCaption ? [linkedInDocCaption] : [],
          headlines: linkedInDocHeadline ? [linkedInDocHeadline] : [],
          descriptions: [] as string[],
          cta: undefined,
          url: linkedInDocUrl,
          legal: linkedInDocLegal,
          ratio: '1x1',
        };
      case 'meta-carousel':
        // Returns feed card payload — used only for isDirty snapshot; actual submits are done in handleSave/handleValidate
        return {
          post_copies: metaCarouselCaption ? [metaCarouselCaption] : [],
          headlines: metaCarouselFeedCardData.map((c) => c.headline),
          descriptions: metaCarouselFeedCardData.map((c) => c.description),
          cta: metaCarouselCta ? [metaCarouselCta] : undefined,
          url: metaCarouselDestinationUrl,
          legal: metaCarouselLegal,
          carousel_items: metaCarouselFeedCardData.map((c, i) => ({
            headline: c.headline,
            description: c.description,
            hasFile: metaCarouselFeedHasFiles[i] ?? false,
          })),
        };
      case 'meta-dynamic-creative':
        return {
          post_copies: metaDCPrimaryTexts.filter(Boolean),
          headlines: metaDCHeadlines.filter(Boolean),
          descriptions: metaDCDescriptions.filter(Boolean),
          cta: metaDCCtas.filter(Boolean).length > 0 ? metaDCCtas.filter(Boolean) : undefined,
          url: metaDCUrl,
          legal: metaDCLegal,
        };
      case 'meta-flexible-ad':
        return {
          post_copies: flexibleCards.map((c) => c.primaryText),
          headlines: flexibleCards.map((c) => c.headline),
          descriptions: flexibleCards.map((c) => c.description),
          cta: flexibleCta ? [flexibleCta] : undefined,
          url: flexibleUrl,
          legal: flexibleLegal,
        };
      case 'meta-collection':
        return {
          post_copies: collectionCaption ? [collectionCaption] : [],
          headlines: collectionProducts.map((p) => p.title ?? ''),
          descriptions: [] as string[],
          cta: collectionCta ? [collectionCta] : undefined,
          url: collectionUrl,
          legal: collectionLegal,
          collection_items: collectionProducts.map((p) => ({
            headline: p.title ?? '',
            description: '',
            hasFile: true,
          })),
        };
      case 'linkedin-carousel':
        return {
          post_copies: linkedInCarouselCaption ? [linkedInCarouselCaption] : [],
          headlines: linkedInCarouselCardData.map((c) => c.headline),
          // Store per-card destination URLs in descriptions[] — no per-card URL field exists,
          // this lets us restore all card URLs after save+refresh.
          descriptions: linkedInCarouselCardData.map((c) => c.destinationUrl),
          cta: undefined,
          // url = first card's URL so the backend validation rule (destination_url required) can check it
          url: linkedInCarouselCardData[0]?.destinationUrl ?? '',
          legal: linkedInCarouselLegal,
          carousel_items: linkedInCarouselCardData.map((c, i) => ({
            headline: c.headline,
            description: '',
            hasFile: linkedInCarouselHasFiles[i] ?? false,
          })),
        };
      default: // meta and all other formats
        return {
          post_copies: metaAllPostCopies.filter(Boolean),
          headlines: metaHeadline ? [metaHeadline] : [],
          descriptions: metaDescription ? [metaDescription] : [],
          cta: metaCta ? [metaCta] : undefined,
          url: metaUrl,
          legal: metaLegal,
        };
    }
  }

  // ── Save / Validate handlers ───────────────────────────────────────────────
  // Two-phase: upload any new image files first, then POST all fields together.
  // Existing images that haven't been replaced are left untouched by the backend
  // (no file ref = no change to that ratio's stored asset).
  async function handleSave(options?: {
    silent?: boolean;
    skipInvalidation?: boolean;
  }): Promise<boolean> {
    if (!campaignId || isSavingRef.current) return false;
    isSavingRef.current = true;
    onSavingChange?.(true);
    try {
      const _invalidate = () => {
        if (options?.skipInvalidation) return;
        queryClient.invalidateQueries({ queryKey: ['campaign-assets', campaignId] });
        queryClient.refetchQueries({ queryKey: ['campaigns'] });
      };
      const va = assetsForVariant(selectedVariant);

      // ── Meta Carousel: two-submit flow (one per ratio) — early return ──────
      // Save story cards (9:16) first for DB consistency (matches handleValidate order).
      if (mockupType === 'meta-carousel') {
        const allDeletedIds = [...metaCarouselFeedDeletedIds, ...metaCarouselStoryDeletedIds];
        if (allDeletedIds.length > 0) {
          await Promise.all(allDeletedIds.map((id) => deleteCampaignAsset(campaignId, id)));
        }
        // Upload + save story cards (9:16) first
        let storyFileRefs: UploadedFileRef[] | undefined;
        if (metaCarouselStoryNewFiles.length > 0) {
          storyFileRefs = await uploadWithProgress(
            metaCarouselStoryNewFiles.map((f) => ({
              file: f,
              ratio: '9x16',
              assetTypeId: assetTypeId,
            })),
          );
        }
        const hasExistingStory = va.some(
          (a) =>
            normalizeRatio(a.ratio) === '9x16' &&
            !!a.storage_path &&
            !metaCarouselStoryDeletedIds.includes(a.id),
        );
        if (storyFileRefs || hasExistingStory) {
          await submitCampaignAssets(campaignId, {
            post_copies: metaCarouselCaption ? [metaCarouselCaption] : [],
            cta: metaCarouselCta ? [metaCarouselCta] : undefined,
            url: metaCarouselDestinationUrl,
            legal: metaCarouselLegal,
            asset_type_id: assetTypeId,
            status: 'draft',
            skip_validation: true,
            variant_number: selectedVariant,
            is_offer_led: isOfferLed,
            offer_end_date: offerEndDate,
            uploaded_file_refs: storyFileRefs,
            ratio: '9x16',
            // Story and feed are saved as separate submits. The backend only
            // repositions IDs present in this list, so each ratio's order is set
            // independently; hydration filters by ratio before sorting, so the
            // index ranges overlapping across ratios is harmless.
            asset_order: buildAssetOrder(metaCarouselStoryOrder, storyFileRefs ?? []),
          });
        }
        // Then upload + save feed cards (1:1)
        let feedFileRefs: UploadedFileRef[] | undefined;
        if (metaCarouselFeedNewFiles.length > 0) {
          feedFileRefs = await uploadWithProgress(
            metaCarouselFeedNewFiles.map((f) => ({
              file: f,
              ratio: '1x1',
              assetTypeId: assetTypeId,
            })),
          );
        }
        await submitCampaignAssets(campaignId, {
          post_copies: metaCarouselCaption ? [metaCarouselCaption] : [],
          headlines: metaCarouselFeedCardData.map((c) => c.headline),
          descriptions: metaCarouselFeedCardData.map((c) => c.description),
          cta: metaCarouselCta ? [metaCarouselCta] : undefined,
          url: metaCarouselDestinationUrl,
          legal: metaCarouselLegal,
          asset_type_id: assetTypeId,
          status: 'draft',
          skip_validation: true,
          variant_number: selectedVariant,
          is_offer_led: isOfferLed,
          offer_end_date: offerEndDate,
          uploaded_file_refs: feedFileRefs,
          ratio: '1x1',
          carousel_items: metaCarouselFeedCardData.map((c) => ({
            headline: c.headline,
            description: c.description,
            hasFile: true,
          })),
          asset_order: buildAssetOrder(metaCarouselFeedOrder, feedFileRefs ?? []),
        });
        // Then the 4:5 crops, in their own submit. Same reason story and feed are
        // split: a submit sets the order for one ratio only. The card text is not
        // repeated here — it lives on the 1:1 primary and is read from there on
        // hydration, so sending it twice would just risk the two copies drifting.
        let feed4x5FileRefs: UploadedFileRef[] | undefined;
        if (metaCarouselFeed4x5NewFiles.length > 0) {
          feed4x5FileRefs = await uploadWithProgress(
            metaCarouselFeed4x5NewFiles.map((f) => ({
              file: f,
              ratio: '4x5',
              assetTypeId: assetTypeId,
            })),
          );
        }
        const hasExistingFeed4x5 = va.some(
          (a) =>
            exactRatio(a.ratio) === '4x5' &&
            !!a.storage_path &&
            !metaCarouselFeedDeletedIds.includes(a.id),
        );
        if (feed4x5FileRefs || hasExistingFeed4x5) {
          await submitCampaignAssets(campaignId, {
            post_copies: metaCarouselCaption ? [metaCarouselCaption] : [],
            cta: metaCarouselCta ? [metaCarouselCta] : undefined,
            url: metaCarouselDestinationUrl,
            legal: metaCarouselLegal,
            asset_type_id: assetTypeId,
            status: 'draft',
            skip_validation: true,
            variant_number: selectedVariant,
            uploaded_file_refs: feed4x5FileRefs,
            ratio: '4x5',
            asset_order: buildAssetOrder(metaCarouselFeed4x5Order, feed4x5FileRefs ?? []),
          });
        }
        setMetaCarouselFeedNewFiles([]);
        setMetaCarouselFeed4x5NewFiles([]);
        setMetaCarouselFeedDeletedIds([]);
        setMetaCarouselStoryNewFiles([]);
        setMetaCarouselStoryDeletedIds([]);
        setUnsavedNewVariants((prev) => prev.filter((n) => n !== selectedVariant));
        setLoadedValues({
          isOfferLed,
          offerEndDate,
          postCopies: metaCarouselCaption ? [metaCarouselCaption] : [],
          headline: '',
          description: '',
          cta: metaCarouselCta,
          url: metaCarouselDestinationUrl,
          legal: metaCarouselLegal,
          carouselHeadlines: metaCarouselFeedCardData.map((c) => c.headline),
          carouselDestUrls: metaCarouselFeedCardData.map((c) => c.description),
        });
        _invalidate();
        if (!options?.silent) toast.success('Saved successfully');
        return true;
      }

      // exactRatio so a 4:5 asset is never mistaken for the 1:1 one — otherwise
      // existingAssetId could point at the wrong row and the upload would
      // overwrite the other slot's file.
      const existing1x1 = va.find((a) => exactRatio(a.ratio) === '1x1');
      const existing4x5 = va.find((a) => exactRatio(a.ratio) === '4x5');
      const existing9x16 = va.find((a) => normalizeRatio(a.ratio) === '9x16');

      let uploadedFileRefs: UploadedFileRef[] | undefined;
      // Set by multi-asset formats to the full display order of their cards.
      let assetOrderTokens: AssetOrderToken[] | undefined;

      if (mockupType === 'meta-dynamic-creative') {
        // Delete removed assets, upload new ones (each image is a separate CampaignAsset row)
        const allDCDeletedIds = [
          ...metaDCDeletedIds1x1,
          ...metaDCDeletedIds4x5,
          ...metaDCDeletedIds9x16,
        ];
        if (allDCDeletedIds.length > 0) {
          await Promise.all(allDCDeletedIds.map((id) => deleteCampaignAsset(campaignId, id)));
        }
        // All three ratios go in ONE submit with their refs concatenated, so each
        // lane's newFileIndex must be offset past every lane before it. The offsets
        // are accumulated from the actual ref counts rather than written out per
        // lane — with three lanes, hand-written offsets are exactly the kind of
        // thing that silently attaches a 9:16 file to a 4:5 slot.
        //
        // Unlike the carousels this is deliberately NOT split into a submit per
        // ratio: a submit sets the order for one ratio, and DC wants one shared
        // order list. Hydration re-filters by ratio before sorting, so a single
        // shared index space is fine.
        const dcLanes: { files: File[]; ratio: UploadRatio; order: AssetOrderToken[] }[] = [
          { files: metaDCNewFiles1x1, ratio: '1x1', order: metaDCOrder1x1 },
          { files: metaDCNewFiles4x5, ratio: '4x5', order: metaDCOrder4x5 },
          { files: metaDCNewFiles9x16, ratio: '9x16', order: metaDCOrder9x16 },
        ];
        const allFileRefs: UploadedFileRef[] = [];
        const dcOrder: AssetOrderToken[] = [];
        for (const lane of dcLanes) {
          const offset = allFileRefs.length;
          if (lane.files.length > 0) {
            allFileRefs.push(
              ...(await uploadWithProgress(
                lane.files.map((f) => ({
                  file: f,
                  ratio: lane.ratio,
                  assetTypeId: assetTypeId,
                })),
              )),
            );
          }
          dcOrder.push(
            ...lane.order.map((t) =>
              'newFileIndex' in t ? { newFileIndex: t.newFileIndex + offset } : t,
            ),
          );
        }
        const savePayload = buildSavePayload();
        await submitCampaignAssets(campaignId, {
          ...savePayload,
          asset_type_id: assetTypeId,
          status: 'draft',
          skip_validation: true,
          variant_number: selectedVariant,
          uploaded_file_refs: allFileRefs.length > 0 ? allFileRefs : undefined,
          is_offer_led: isOfferLed,
          offer_end_date: offerEndDate,
          asset_order: buildAssetOrder(dcOrder, allFileRefs),
        });
        setMetaDCNewFiles1x1([]);
        setMetaDCNewFiles4x5([]);
        setMetaDCNewFiles9x16([]);
        setMetaDCDeletedIds1x1([]);
        setMetaDCDeletedIds4x5([]);
        setMetaDCDeletedIds9x16([]);
        setUnsavedNewVariants((prev) => prev.filter((n) => n !== selectedVariant));
        setLoadedValues({
          isOfferLed,
          offerEndDate,
          postCopies: metaDCPrimaryTexts.filter(Boolean),
          headline: metaDCHeadlines[0] ?? '',
          description: metaDCDescriptions[0] ?? '',
          cta: metaDCCta,
          url: metaDCUrl,
          legal: metaDCLegal,
          dcHeadlines: metaDCHeadlines.filter(Boolean),
          dcDescriptions: metaDCDescriptions.filter(Boolean),
          dcCtas: metaDCCtas.filter(Boolean),
        });
        _invalidate();
        if (!options?.silent) toast.success('Saved successfully');
        return true;
      }

      if (mockupType === 'meta-flexible-ad') {
        if (flexibleDeletedIds.length > 0) {
          await Promise.all(flexibleDeletedIds.map((id) => deleteCampaignAsset(campaignId, id)));
        }
        const newFileCards = flexibleCards.filter((c) => c.file !== null);
        const fileRefs: UploadedFileRef[] =
          newFileCards.length > 0
            ? await uploadWithProgress(
                newFileCards.map((c) => ({
                  file: c.file!,
                  assetTypeId: assetTypeId,
                })),
              )
            : [];
        // flexibleCards is already the full ordered list (each slot carries either
        // an existingAssetId or a new file), so derive the order tokens directly.
        let flexNewIdx = 0;
        const flexibleOrder: AssetOrderToken[] = flexibleCards
          .filter((c) => c.file !== null || c.existingAssetId)
          .map((c) =>
            c.file !== null
              ? { newFileIndex: flexNewIdx++ }
              : { existingAssetId: c.existingAssetId! },
          );
        const savePayload = buildSavePayload();
        await submitCampaignAssets(campaignId, {
          ...savePayload,
          asset_type_id: assetTypeId,
          status: 'draft',
          skip_validation: true,
          variant_number: selectedVariant,
          uploaded_file_refs: fileRefs.length > 0 ? fileRefs : undefined,
          is_offer_led: isOfferLed,
          offer_end_date: offerEndDate,
          asset_order: buildAssetOrder(flexibleOrder, fileRefs),
        });
        setFlexibleDeletedIds([]);
        setFlexibleCards((prev) => prev.map((c) => ({ ...c, file: null })));
        setUnsavedNewVariants((prev) => prev.filter((n) => n !== selectedVariant));
        setLoadedValues({
          isOfferLed,
          offerEndDate,
          postCopies: flexibleCards.map((c) => c.primaryText),
          headline: flexibleCards[0]?.headline ?? '',
          description: flexibleCards[0]?.description ?? '',
          cta: flexibleCta,
          url: flexibleUrl,
          legal: flexibleLegal,
          dcHeadlines: flexibleCards.map((c) => c.headline),
          dcDescriptions: flexibleCards.map((c) => c.description),
        });
        _invalidate();
        if (!options?.silent) toast.success('Saved successfully');
        return true;
      }

      if (mockupType === 'meta-collection') {
        // For collection: first 1x1 asset in display order = hero; the rest are products
        const all1x1Sorted = va
          .filter((a) => normalizeRatio(a.ratio) === '1x1' && !!a.storage_path)
          .sort(byDisplayOrder);
        const existingHero1x1 = all1x1Sorted[0];
        // Delete removed hero and product images
        const allCollectionDeletedIds = [...pendingDeleteAssetIds, ...collectionProductDeletedIds];
        if (allCollectionDeletedIds.length > 0) {
          await Promise.all(
            allCollectionDeletedIds.map((id) => deleteCampaignAsset(campaignId, id)),
          );
        }
        // Upload new product images (1x1, no existingAssetId — always new records)
        const newProductFiles = collectionProductFiles.filter((f): f is File => f !== null);
        let productFileRefs: UploadedFileRef[] | undefined;
        if (newProductFiles.length > 0) {
          productFileRefs = await uploadWithProgress(
            newProductFiles.map((f) => ({
              file: f,
              ratio: '1x1',
              assetTypeId: assetTypeId,
            })),
          );
        }
        // Upload hero cover if changed (one image shared across all placements)
        let heroFileRef: UploadedFileRef | undefined;
        if (file1x1) {
          const refs = await uploadWithProgress([
            {
              file: file1x1,
              ratio: '1x1',
              assetTypeId: assetTypeId,
              existingAssetId: existingHero1x1?.id,
            },
          ]);
          heroFileRef = refs[0];
        }
        const allFileRefs = [...(heroFileRef ? [heroFileRef] : []), ...(productFileRefs ?? [])];
        // Collection order = hero first, then the product slots in slot order.
        // Product slots are index-aligned across collectionProductFiles (new
        // uploads) and collectionInitialProducts (existing rows), so walking the
        // slot indexes yields the on-screen order. New-file indexes are dense
        // within allFileRefs, which is why the hero (when re-uploaded) occupies
        // index 0 and products follow.
        const heroOffset = heroFileRef ? 1 : 0;
        const collectionOrder: AssetOrderToken[] = [];
        if (heroFileRef) {
          collectionOrder.push({ newFileIndex: 0 });
        } else if (existingHero1x1?.id) {
          collectionOrder.push({ existingAssetId: existingHero1x1.id });
        }
        let productNewIdx = 0;
        const slotCount = Math.max(collectionProductFiles.length, collectionInitialProducts.length);
        for (let i = 0; i < slotCount; i++) {
          const newFile = collectionProductFiles[i];
          if (newFile) {
            collectionOrder.push({ newFileIndex: heroOffset + productNewIdx++ });
            continue;
          }
          const existingId = collectionInitialProducts[i]?.existingAssetId;
          if (existingId && !collectionProductDeletedIds.includes(existingId)) {
            collectionOrder.push({ existingAssetId: existingId });
          }
        }
        const savePayload = buildSavePayload();
        await submitCampaignAssets(campaignId, {
          ...savePayload,
          asset_type_id: assetTypeId,
          status: 'draft',
          skip_validation: true,
          variant_number: selectedVariant,
          uploaded_file_refs: allFileRefs.length > 0 ? allFileRefs : undefined,
          is_offer_led: isOfferLed,
          offer_end_date: offerEndDate,
          asset_order: buildAssetOrder(collectionOrder, allFileRefs),
        });
        setFile1x1(null);
        setFile4x5(null);
        setFile9x16(null);
        setPendingDeleteAssetIds([]);
        setCollectionProductFiles([]);
        setCollectionProductDeletedIds([]);
        setUnsavedNewVariants((prev) => prev.filter((n) => n !== selectedVariant));
        setLoadedValues({
          isOfferLed,
          offerEndDate,
          postCopies: collectionCaption ? [collectionCaption] : [],
          headline: '',
          description: '',
          cta: collectionCta,
          url: collectionUrl,
          legal: collectionLegal,
        });
        _invalidate();
        if (!options?.silent) toast.success('Saved successfully');
        return true;
      }

      if (mockupType === 'snapchat-story') {
        // Delete removed story cards first
        if (snapchatStoryDeletedIds.length > 0) {
          await Promise.all(
            snapchatStoryDeletedIds.map((id) => deleteCampaignAsset(campaignId, id)),
          );
        }
        // Upload each new card file as a separate 9:16 CampaignAsset
        if (snapchatStoryNewFiles.length > 0) {
          uploadedFileRefs = await uploadWithProgress(
            snapchatStoryNewFiles.map((f) => ({
              file: f,
              ratio: '9x16',
              assetTypeId: assetTypeId,
            })),
          );
        }
        assetOrderTokens = snapchatStoryOrder;
      } else if (mockupType === 'linkedin') {
        if (pendingDeleteAssetIds.length > 0) {
          await Promise.all(pendingDeleteAssetIds.map((id) => deleteCampaignAsset(campaignId, id)));
        }
        // Two independent feed slots, same as Meta Single. Each carries its own
        // existingAssetId (resolved via exactRatio) so uploading one never
        // overwrites the other's row.
        const liFiles: { file: File; ratio: string; existingAssetId?: string }[] = [];
        if (linkedInFile)
          liFiles.push({ file: linkedInFile, ratio: '1x1', existingAssetId: existing1x1?.id });
        if (linkedInFile4x5)
          liFiles.push({ file: linkedInFile4x5, ratio: '4x5', existingAssetId: existing4x5?.id });
        if (liFiles.length > 0) {
          uploadedFileRefs = await uploadWithProgress(
            liFiles.map((f) => ({
              file: f.file,
              ratio: f.ratio,
              assetTypeId: assetTypeId,
              existingAssetId: f.existingAssetId,
            })),
          );
        }
      } else if (mockupType === 'linkedin-document') {
        if (pendingDeleteAssetIds.length > 0) {
          await Promise.all(pendingDeleteAssetIds.map((id) => deleteCampaignAsset(campaignId, id)));
        }
        if (linkedInDocFile) {
          uploadedFileRefs = await uploadWithProgress([
            {
              file: linkedInDocFile,
              ratio: '1x1',
              assetTypeId: assetTypeId,
              existingAssetId: existing1x1?.id,
            },
          ]);
        }
      } else if (mockupType === 'linkedin-carousel') {
        if (linkedInCarouselDeletedIds.length > 0) {
          await Promise.all(
            linkedInCarouselDeletedIds.map((id) => deleteCampaignAsset(campaignId, id)),
          );
        }
        if (linkedInCarouselNewFiles.length > 0) {
          uploadedFileRefs = await uploadWithProgress(
            linkedInCarouselNewFiles.map((f) => ({
              file: f,
              ratio: '1x1',
              assetTypeId: assetTypeId,
            })),
          );
        }
        assetOrderTokens = linkedInCarouselOrder;
      } else {
        // Delete any images the user cleared before uploading new ones
        if (pendingDeleteAssetIds.length > 0) {
          await Promise.all(pendingDeleteAssetIds.map((id) => deleteCampaignAsset(campaignId, id)));
        }

        // Upload any new files individually (avoids Cloud Run 32 MB limit)
        const filesToUpload: {
          file: File;
          ratio: string;
          existingAssetId?: string;
        }[] = [];
        if (file1x1)
          filesToUpload.push({
            file: file1x1,
            ratio: '1x1',
            existingAssetId: existing1x1?.id,
          });
        if (file4x5)
          filesToUpload.push({
            file: file4x5,
            ratio: '4x5',
            existingAssetId: existing4x5?.id,
          });
        if (file9x16)
          filesToUpload.push({
            file: file9x16,
            ratio: '9x16',
            existingAssetId: existing9x16?.id,
          });

        if (filesToUpload.length > 0) {
          uploadedFileRefs = await uploadWithProgress(
            filesToUpload.map((f) => ({
              file: f.file,
              ratio: f.ratio,
              assetTypeId: assetTypeId,
              existingAssetId: f.existingAssetId,
            })),
          );
        }
      }

      const savePayload = buildSavePayload();
      await submitCampaignAssets(campaignId, {
        ...savePayload,
        asset_type_id: assetTypeId,
        status: 'draft',
        skip_validation: true,
        variant_number: selectedVariant,
        is_offer_led: isOfferLed,
        offer_end_date: offerEndDate,
        uploaded_file_refs: uploadedFileRefs,
        // Only multi-asset formats track an explicit order; single-asset formats
        // leave this undefined so the backend keeps its default positioning.
        asset_order: assetOrderTokens
          ? buildAssetOrder(assetOrderTokens, uploadedFileRefs ?? [])
          : undefined,
      });

      // The LinkedIn carousel's 4:5 crops go in their own submit, for the same
      // reason the Meta carousel splits them: a submit sets the order for one ratio
      // only. Card text is not repeated here — it lives on the 1:1 primary and is
      // read from there on hydration, so sending it twice would risk the two copies
      // drifting.
      if (mockupType === 'linkedin-carousel') {
        let li4x5FileRefs: UploadedFileRef[] | undefined;
        if (linkedInCarousel4x5NewFiles.length > 0) {
          li4x5FileRefs = await uploadWithProgress(
            linkedInCarousel4x5NewFiles.map((f) => ({
              file: f,
              ratio: '4x5',
              assetTypeId: assetTypeId,
            })),
          );
        }
        const hasExistingLi4x5 = assetsForVariant(selectedVariant).some(
          (a) =>
            exactRatio(a.ratio) === '4x5' &&
            !!a.storage_path &&
            !linkedInCarouselDeletedIds.includes(a.id),
        );
        if (li4x5FileRefs || hasExistingLi4x5) {
          await submitCampaignAssets(campaignId, {
            post_copies: savePayload.post_copies,
            url: savePayload.url,
            legal: savePayload.legal,
            asset_type_id: assetTypeId,
            status: 'draft',
            skip_validation: true,
            variant_number: selectedVariant,
            uploaded_file_refs: li4x5FileRefs,
            ratio: '4x5',
            asset_order: buildAssetOrder(linkedInCarousel4x5Order, li4x5FileRefs ?? []),
          });
        }
      }

      // Clear file state and optimistically snapshot the saved values so isDirty
      // goes false immediately — without waiting for the invalidateQueries refetch.
      if (mockupType === 'snapchat-story') {
        setSnapchatStoryNewFiles([]);
        setSnapchatStoryDeletedIds([]);
      } else if (mockupType === 'linkedin') {
        setLinkedInFile(null);
        setLinkedInFile4x5(null);
        setPendingDeleteAssetIds([]);
      } else if (mockupType === 'linkedin-document') {
        setLinkedInDocFile(null);
        setPendingDeleteAssetIds([]);
      } else if (mockupType === 'linkedin-carousel') {
        setLinkedInCarouselNewFiles([]);
        setLinkedInCarousel4x5NewFiles([]);
        setLinkedInCarouselDeletedIds([]);
      } else {
        setFile1x1(null);
        setFile4x5(null);
        setFile9x16(null);
        setPendingDeleteAssetIds([]);
      }
      setUnsavedNewVariants((prev) => prev.filter((n) => n !== selectedVariant));
      setLoadedValues({
        isOfferLed,
        offerEndDate,
        postCopies: savePayload.post_copies,
        headline: savePayload.headlines[0] ?? '',
        description: savePayload.descriptions[0] ?? '',
        cta: savePayload.cta?.[0] ?? '',
        url: savePayload.url,
        legal: savePayload.legal,
        carouselHeadlines: mockupType === 'linkedin-carousel' ? savePayload.headlines : undefined,
        carouselDestUrls: mockupType === 'linkedin-carousel' ? savePayload.descriptions : undefined,
      });
      _invalidate();
      if (!options?.silent) toast.success('Saved successfully');
      return true;
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Save failed — please try again', {
        duration: 6000,
      });
      throw err; // re-throw so Save & Leave can handle failure
    } finally {
      isSavingRef.current = false;
      onSavingChange?.(false);
    }
  }

  // ── Validate handler ──────────────────────────────────────────────────────
  // Always saves first (to persist deletes/uploads/text), then runs validation
  // on the already-saved state via validateVariant. This prevents stale asset ID
  // issues where deleted IDs no longer exist by the time validation runs.
  async function handleValidate() {
    if (!campaignId) return;
    setIsValidatingLocal(true);
    onValidatingChange?.(true);
    try {
      // Step 1: Save first — handles all deletes, uploads, and text saves.
      // handleSave returns false if a save is already in progress (guard against stale state).
      const saveSuccess = await handleSave({ silent: true, skipInvalidation: true });
      if (!saveSuccess) {
        toast.error('Save is already in progress — try again', { duration: 3000 });
        return;
      }

      // Step 2: Run validation on the saved state
      const response = await validateVariant(campaignId, selectedVariant);
      const validation = response.validation;

      // Step 3: Map validation results to field issues
      const normalizeField = (field: string): string => {
        // "ratio_items.feed" ("1:1 or 4:5") is passed through unmapped on purpose —
        // both feed slots subscribe to it directly. See the other normalizeField.
        // Both carousels index their per-card text. Omitting linkedin-carousel here
        // dropped every `headlines[i]` down to the shared `headline` key, so all
        // cards' AI warnings piled onto card 1 — while the sibling normalizeField
        // (which does handle it) spread them correctly. Keep the two in step.
        if (mockupType === 'meta-carousel' || mockupType === 'linkedin-carousel') {
          if (/^post_copies\[/.test(field)) return 'primary_text';
          const hm = field.match(/^headlines\[(\d+)\]/);
          if (hm) return `headline.${hm[1]}`;
          const dm = field.match(/^descriptions\[(\d+)\]/);
          if (dm) return `description.${dm[1]}`;
          const ch = field.match(/^carousel_items\[(\d+)\]\.headline/);
          if (ch) return `headline.${ch[1]}`;
          const cd = field.match(/^carousel_items\[(\d+)\]\.description/);
          if (cd) return `description.${cd[1]}`;
          const cf = field.match(/^carousel_items\[(\d+)\]\.hasFile/);
          if (cf) return `card_image.${cf[1]}`;
          if (field === 'carousel_items.headline') return 'headline';
          if (field === 'carousel_items.description') return 'description';
          return field;
        }
        if (mockupType === 'meta') {
          const pm = field.match(/^post_copies\[(\d+)\]/);
          if (pm) return `primary_text.${pm[1]}`;
          if (field === 'primary_text') return 'primary_text.0';
        }
        if (mockupType === 'meta-dynamic-creative' || mockupType === 'meta-flexible-ad') {
          // DC/Flexible: preserve variation index for per-row inline issues
          const pm = field.match(/^post_copies\[(\d+)\]/);
          if (pm) return `primary_text.${pm[1]}`;
          const hm = field.match(/^headlines\[(\d+)\]/);
          if (hm) return `headline.${hm[1]}`;
          const dm = field.match(/^descriptions\[(\d+)\]/);
          if (dm) return `description.${dm[1]}`;
          return field;
        }
        if (/^post_copies\[/.test(field)) return 'primary_text';
        if (/^headlines\[/.test(field)) return 'headline';
        if (/^descriptions\[/.test(field)) return 'description';
        return field;
      };

      const issues: Record<string, FieldIssue> = {};
      const addToField = (
        field: string,
        type: 'error' | 'warning',
        message: string,
        rule?: string,
        originalField?: string,
      ) => {
        if (!issues[field]) issues[field] = { errors: [], warnings: [], warningMeta: [] };
        if (type === 'error') {
          issues[field].errors.push(message);
        } else {
          const idx = issues[field].warnings.length;
          issues[field].warnings.push(message);
          if (rule) {
            issues[field].warningMeta!.push({
              rule,
              field: originalField ?? field,
              key: `${rule}::${originalField ?? field}::${idx}`,
            });
          }
        }
      };

      if (validation) {
        if (import.meta.env.DEV) {
          console.group('[Validate] Raw response from backend');
          console.log('Errors:', validation.errors);
          console.log('Warnings:', validation.warnings);
          console.groupEnd();
        }

        for (const e of validation.errors) addToField(normalizeField(e.field), 'error', e.message);
        for (const w of validation.warnings)
          addToField(normalizeField(w.field), 'warning', w.message, w.rule, w.field);

        if (import.meta.env.DEV) {
          console.group('[Validate] Normalized fieldIssues');
          console.log(issues);
          console.groupEnd();
        }
      }

      // Step 4: Report results
      if (!validation) {
        toast('Validation was skipped — check required fields and try again', {
          icon: 'ℹ️',
          duration: 5000,
        });
        await queryClient.invalidateQueries({
          queryKey: ['campaign-assets', campaignId],
        });
      } else {
        onValidationResult?.(issues);
        const hasErrors = Object.values(issues).some((fi) => fi.errors.length > 0);
        const hasWarnings = Object.values(issues).some((fi) => fi.warnings.length > 0);
        onValidationComplete?.(hasErrors, hasWarnings);
        const errCount = validation.errors.length;
        const warnCount = validation.warnings.length;
        if (errCount === 0 && warnCount === 0) {
          toast.success('All checks passed for this variant');
        } else {
          const parts: string[] = [];
          if (errCount > 0) parts.push(`${errCount} error${errCount !== 1 ? 's' : ''}`);
          if (warnCount > 0) parts.push(`${warnCount} warning${warnCount !== 1 ? 's' : ''}`);
          const msg = parts.join(', ') + ' found';
          if (errCount > 0) toast.error(msg, { duration: 5000 });
          else toast(msg, { icon: '⚠️', duration: 5000 });
        }

        // Validate all other saved variants sequentially
        const otherSavedVariants = activeVariants.filter(
          (v) => v.saved && v.number !== selectedVariant,
        );
        if (otherSavedVariants.length > 0) {
          let otherErrors = 0;
          for (const v of otherSavedVariants) {
            try {
              const res = await validateVariant(campaignId!, v.number);
              if (res.validation && !res.validation.is_valid) otherErrors++;
            } catch (e) {
              console.error(`Failed to validate variant ${v.number}:`, e);
              otherErrors++;
            }
          }
          if (otherErrors > 0) {
            toast.error(
              `${otherErrors} other variant${otherErrors !== 1 ? 's have' : ' has'} errors — check variant tabs`,
              { duration: 5000 },
            );
          }
        }
        await queryClient.invalidateQueries({
          queryKey: ['campaign-assets', campaignId],
        });
        queryClient.refetchQueries({ queryKey: ['campaigns'] });
      }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Validation failed — please try again', {
        duration: 6000,
      });
      throw err;
    } finally {
      setIsValidatingLocal(false);
      onValidatingChange?.(false);
    }
  }

  // ── AI feedback handler ──────────────────────────────────────────────────
  const handleAIFeedback = useCallback(
    async (warningKey: string, warningMessage: string, action: 'accepted' | 'dismissed') => {
      if (!campaignId) return;
      const variantAssets = allAssets.filter((a) => (a.variant_number ?? 1) === selectedVariant);
      const assetWithValidation = variantAssets.find((a) => a.validation_result);
      if (!assetWithValidation) return;

      setFeedbackPending((prev) => new Set(prev).add(warningKey));
      try {
        const response = await submitAIFeedback(campaignId, assetWithValidation.id, {
          warning_key: warningKey,
          warning_message: warningMessage,
          action,
        });
        if (response.success) {
          setAiFeedback(response.validation_result.feedback ?? {});
        }
      } catch (err) {
        console.error('Failed to submit AI feedback:', err);
      } finally {
        setFeedbackPending((prev) => {
          const next = new Set(prev);
          next.delete(warningKey);
          return next;
        });
      }
    },
    [campaignId, allAssets, selectedVariant],
  );

  // ── Approve handler ───────────────────────────────────────────────────────
  async function handleApprove(note: string) {
    if (!campaignId) return;
    onApprovingChange?.(true);
    try {
      await approveAllVariants(campaignId, note);
      queryClient.refetchQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({
        queryKey: ['campaign-assets', campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ['campaign-events', campaignId],
      });
      toast.success('Assets approved');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Approval failed — please try again', {
        duration: 6000,
      });
      throw err;
    } finally {
      onApprovingChange?.(false);
    }
  }

  // Keep the refs in sync so the header buttons can call the right handler at any time
  if (saveFnRef) saveFnRef.current = () => handleSave().then(() => {});
  if (validateFnRef) validateFnRef.current = handleValidate;
  if (approveFnRef) approveFnRef.current = handleApprove;

  return (
    <>
      {/* ── Delete variant confirmation modal ───────────────────────── */}
      {pendingDelete !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete Variant
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-slate-800">Variant {pendingDelete}</span>? This
                cannot be undone.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setPendingDelete(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmRemoveVariant(pendingDelete)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete all assets confirmation modal ──────────────────────── */}
      {showDeleteAllConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-all-title"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isDeletingAll) setShowDeleteAllConfirm(false);
          }}
        >
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p
                id="delete-all-title"
                className="text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Delete All Assets
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                This will permanently delete{' '}
                <span className="font-semibold text-slate-800">all assets and variants</span> for
                this campaign. This action cannot be undone.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setShowDeleteAllConfirm(false)}
                  disabled={isDeletingAll}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAllAssets}
                  disabled={isDeletingAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {isDeletingAll ? 'Deleting…' : 'Delete All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col min-h-0 min-w-0 overflow-hidden">
        {/* ── Shared header — same height/style as all other section headers ── */}
        <div className="px-4 h-11 border-b border-slate-200 bg-slate-50 rounded-t-xl flex items-center gap-2 flex-shrink-0">
          {/* Format label — clickable pill when onBack is available, plain text otherwise */}
          {onBack ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onBack}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 hover:bg-slate-200 transition-colors group/fmt shrink-0"
                >
                  <RepeatIcon className="w-3 h-3 text-slate-400 group-hover/fmt:text-slate-600 transition-colors" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    {formatLabel}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Change ad format</TooltipContent>
            </Tooltip>
          ) : (
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 shrink-0">
              {formatLabel}
            </p>
          )}

          <div className="flex-1" />

          {/* Variant tabs — only shown when 2+ variants exist; hidden for DC (variants don't apply) */}
          {multipleVariants && !isDC && (
            <div className="flex items-center">
              {activeVariants.map((v) => (
                <div key={v.number} className="relative group/tab">
                  {
                    <>
                      <button
                        onClick={() => switchVariant(v.number)}
                        className={`px-3 py-1 text-xs transition-all border-b-2 ${
                          selectedVariant === v.number
                            ? 'text-[#0014cc] font-semibold border-[#0014cc]'
                            : v.saved
                              ? 'text-slate-500 font-medium border-transparent hover:text-slate-700 hover:border-slate-300'
                              : 'text-slate-400 italic border-transparent hover:text-slate-500 hover:border-slate-200'
                        }`}
                      >
                        {v.saved ? `Variant ${v.number}` : `Variant ${v.number}*`}
                        <ValidationStatusIcon status={variantValidationStatus[v.number]} />
                      </button>
                      {!readOnly && (
                        <button
                          onClick={() => requestRemoveVariant(v)}
                          className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-slate-300 hover:bg-red-400 text-white flex items-center justify-center opacity-0 group-hover/tab:opacity-100 transition-opacity"
                        >
                          <svg
                            className="w-2 h-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </>
                  }
                </div>
              ))}
            </div>
          )}

          {/* Add Variant button — visible when not read-only and < max; hidden for DC */}
          {!readOnly && !isDC && activeVariants.length < MAX_VARIANTS && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    onClick={canAddVariant ? addVariant : undefined}
                    aria-disabled={!canAddVariant}
                    className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg bg-white border transition-all ${
                      !canAddVariant
                        ? 'text-slate-300 border-slate-100 cursor-not-allowed'
                        : 'text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700'
                    }`}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Variant
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {canAddVariant ? 'Add a new variant' : 'Save the current variant first'}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Delete All Assets button — visible when not read-only and there's something to clear */}
          {!readOnly && (allAssets.length > 0 || campaign?.asset_type_id) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowDeleteAllConfirm(true)}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg bg-white text-slate-500 border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Delete All
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Delete all assets for this campaign</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Variant count hint — at least RECOMMENDED_MIN_VARIANTS variants suggested (not for Dynamic Creative) */}
        {!readOnly &&
          !isDC &&
          apiVariantNumbers.length > 0 &&
          apiVariantNumbers.length < RECOMMENDED_MIN_VARIANTS && (
            <div className="mx-4 mb-1 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <svg
                className="w-4 h-4 text-amber-600 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-amber-800">
                We recommend creating at least {RECOMMENDED_MIN_VARIANTS} variants for better ad
                performance.
              </p>
            </div>
          )}

        {/* ── Two-column content area ─────────────────────────────────── */}
        <div className="flex-1 flex min-h-0">
          {/* Left: Mockup preview — ~43% to maintain original 30% of full page */}
          <div className="w-[43%] border-r border-slate-100 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto hover-scroll min-h-0 px-1 py-3">
              <AnimatePresence mode="wait">
                {showSkeleton ? (
                  /* ── Loading skeleton — shown while assets fetch + blob URLs resolve ── */
                  <motion.div
                    key="mockup-skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col items-center justify-center gap-0 min-h-full"
                  >
                    <Skeleton
                      className={`rounded-xl ${
                        isStoryFormat
                          ? 'w-[360px] h-[640px]'
                          : isCollection
                            ? 'w-[288px] h-[650px]'
                            : isLinkedIn
                              ? 'w-[350px] h-[480px]'
                              : 'w-[375px] h-[560px]'
                      }`}
                    />
                  </motion.div>
                ) : (
                  /* ── Real mockup ── */
                  <motion.div
                    key="mockup-content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center justify-center gap-0 min-h-full"
                  >
                    {/* Ratio toggle — sits directly above the mockup */}
                    {ratios && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center border-b border-slate-200 mb-4 cursor-default">
                            {ratios.map((r) => (
                              <button
                                key={r}
                                onClick={() => setSelectedRatio(r)}
                                className={`w-28 text-center py-1.5 text-sm border-b-2 -mb-px transition-all ${
                                  selectedRatio === r
                                    ? 'text-[#0014cc] font-semibold border-[#0014cc]'
                                    : 'text-slate-500 font-medium border-transparent hover:text-slate-700 hover:border-slate-300'
                                }`}
                              >
                                {RATIO_LABELS[r]}
                              </button>
                            ))}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-72 text-center">
                          Meta lets you upload separate assets for each placement. Switch here to
                          preview how each will look — 1:1 for Feed, 9:16 for Story, and 9:16 for
                          Reels.
                        </TooltipContent>
                      </Tooltip>
                    )}

                    <div
                      className="overflow-hidden rounded-lg ring-1 ring-slate-300 shadow-sm"
                      style={{
                        zoom: 0.9,
                        ...(isStoryFormat
                          ? { width: '360px', height: '640px' }
                          : isCollection
                            ? {
                                width: '288px',
                                height: '650px',
                                position: 'relative' as const,
                              }
                            : isLinkedIn
                              ? { width: '350px' }
                              : {}),
                      }}
                    >
                      {/* Collection: scale the 400px-wide mockup down to fit without scrolling */}
                      {isCollection ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transform: 'scale(0.72)',
                            transformOrigin: 'top left',
                            width: '400px',
                          }}
                        >
                          <MockupPreview
                            type={effectiveType}
                            imageUrl1x1={effectiveImageUrl1x1}
                            imageUrl9x16={effectiveImageUrl9x16}
                            contentType1x1={feedContentType}
                            contentType9x16={contentType9x16}
                            metaCaption={effectiveCaption}
                            metaHeadline={effectiveHeadline}
                            metaDescription={effectiveDescription}
                            metaCta={effectiveCta}
                            metaCarouselCaption={metaCarouselCaption}
                            metaCarouselCta={metaCarouselCta}
                            metaCarouselFeedItems={metaCarouselFeedMockupItems}
                            metaCarouselStoryItems={metaCarouselStoryItems}
                            onMetaCarouselFeedCardChange={setMetaCarouselFeedActiveCard}
                            onMetaCarouselStoryCardChange={setMetaCarouselStoryActiveCard}
                            metaCarouselFeedActiveIndex={metaCarouselFeedActiveCard}
                            metaCarouselStoryActiveIndex={metaCarouselStoryActiveCard}
                            snapchatHeadline={snapchatHeadline}
                            snapchatStoryHeadline={snapchatStoryHeadline}
                            snapchatStoryItems={snapchatStoryItems}
                            snapchatActiveIndex={snapchatActiveCard}
                            onSnapchatSlideChange={setSnapchatActiveCard}
                            linkedInImageUrl={linkedInImageUrl}
                            linkedInCaption={linkedInCaption}
                            linkedInHeadline={linkedInHeadline}
                            linkedInCta={linkedInCta}
                            linkedInWebsite={linkedInWebsite}
                            linkedInDocCaption={linkedInDocCaption}
                            linkedInDocHeadline={linkedInDocHeadline}
                            linkedInDocPageImage={linkedInDocPageImage}
                            linkedInCarouselCaption={linkedInCarouselCaption}
                            linkedInCarouselItems={linkedInCarouselMockupItems}
                            linkedInCarouselActiveIndex={linkedInCarouselActiveCard}
                            onLinkedInCarouselCardChange={setLinkedInCarouselActiveCard}
                            collectionCaption={collectionCaption}
                            collectionProducts={collectionProducts}
                            collectionCta={collectionCta}
                            collectionWebsite={collectionWebsite}
                            tikTokCaption={tikTokCaption}
                            tikTokCta={tikTokCta}
                          />
                        </div>
                      ) : (
                        <MockupPreview
                          type={effectiveType}
                          imageUrl1x1={effectiveImageUrl1x1}
                          imageUrl9x16={effectiveImageUrl9x16}
                          contentType1x1={feedContentType}
                          contentType9x16={contentType9x16}
                          metaCaption={effectiveCaption}
                          metaHeadline={effectiveHeadline}
                          metaDescription={effectiveDescription}
                          metaCta={effectiveCta}
                          metaCarouselCaption={metaCarouselCaption}
                          metaCarouselCta={metaCarouselCta}
                          metaCarouselFeedItems={metaCarouselFeedMockupItems}
                          metaCarouselStoryItems={metaCarouselStoryItems}
                          onMetaCarouselFeedCardChange={setMetaCarouselFeedActiveCard}
                          onMetaCarouselStoryCardChange={setMetaCarouselStoryActiveCard}
                          metaCarouselFeedActiveIndex={metaCarouselFeedActiveCard}
                          metaCarouselStoryActiveIndex={metaCarouselStoryActiveCard}
                          snapchatHeadline={snapchatHeadline}
                          snapchatStoryHeadline={snapchatStoryHeadline}
                          snapchatStoryItems={snapchatStoryItems}
                          snapchatActiveIndex={snapchatActiveCard}
                          onSnapchatSlideChange={setSnapchatActiveCard}
                          linkedInImageUrl={linkedInImageUrl}
                          linkedInCaption={linkedInCaption}
                          linkedInHeadline={linkedInHeadline}
                          linkedInCta={linkedInCta}
                          linkedInWebsite={linkedInWebsite}
                          linkedInDocCaption={linkedInDocCaption}
                          linkedInDocHeadline={linkedInDocHeadline}
                          linkedInDocPageImage={linkedInDocPageImage}
                          linkedInCarouselCaption={linkedInCarouselCaption}
                          linkedInCarouselItems={linkedInCarouselMockupItems}
                          linkedInCarouselActiveIndex={linkedInCarouselActiveCard}
                          onLinkedInCarouselCardChange={setLinkedInCarouselActiveCard}
                          collectionCaption={collectionCaption}
                          collectionProducts={collectionProducts}
                          collectionCta={collectionCta}
                          collectionWebsite={collectionWebsite}
                          tikTokCaption={tikTokCaption}
                          tikTokCta={tikTokCta}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Disclaimer */}
            <p className="text-[10px] text-slate-400 text-center italic px-6 pb-3 leading-relaxed flex-shrink-0">
              This is a mock-up and does not represent exact platform previews. Layout and character
              wrapping may differ from the live platform.
            </p>
          </div>

          {/* Right: Ad details form */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            <AnimatePresence mode="wait">
              {showSkeleton ? (
                /* ── Form skeleton — matches the rough shape of MetaSingleDetails ── */
                <motion.div
                  key="form-skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 overflow-y-auto px-6 py-5 space-y-6"
                >
                  {/* About blurb */}
                  <div className="space-y-2">
                    <Skeleton className="h-2.5 w-20 rounded" />
                    <Skeleton className="h-3 w-full rounded" />
                    <Skeleton className="h-3 w-4/5 rounded" />
                  </div>
                  {/* Image cell pair */}
                  <div className="grid grid-cols-2 gap-6">
                    <Skeleton className="h-36 rounded-xl" />
                    <Skeleton className="h-36 rounded-xl" />
                  </div>
                  {/* Primary text field */}
                  <div className="space-y-2">
                    <Skeleton className="h-2.5 w-24 rounded" />
                    <Skeleton className="h-20 rounded-lg" />
                  </div>
                  {/* 2×2 field grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <Skeleton className="h-9 rounded-lg" />
                    <Skeleton className="h-9 rounded-lg" />
                    <Skeleton className="h-9 rounded-lg" />
                    <Skeleton className="h-9 rounded-lg" />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="form-content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col min-h-0 min-w-0"
                >
                  <fieldset
                    disabled={readOnly}
                    className={`flex-1 flex flex-col min-h-0 min-w-0${readOnly ? ' opacity-60' : ''}`}
                  >
                    {/* Offer-led sits above AdDetailsContent so every ad format
                        gets it without per-format wiring, and reads as the single
                        toggle for the whole ad on Dynamic Creative. */}
                    <div className="px-6 pt-5 pb-5 border-b border-slate-100 flex-shrink-0">
                      <OfferLedSection
                        isOfferLed={isOfferLed}
                        offerEndDate={offerEndDate}
                        onOfferLedChange={setIsOfferLed}
                        onOfferEndDateChange={setOfferEndDate}
                        fieldIssues={fieldIssuesProp ?? {}}
                        showErrors={showErrors}
                        showWarnings={showWarnings}
                      />
                    </div>
                    <AdDetailsContent
                      campaignId={campaignId}
                      fordshareIds={fordshareIds}
                      campaign={campaign}
                      mockupType={mockupType}
                      showErrors={showErrors}
                      showWarnings={showWarnings}
                      fieldIssues={fieldIssuesProp ?? {}}
                      feedback={aiFeedback}
                      feedbackPending={feedbackPending}
                      onAIFeedback={handleAIFeedback}
                      usedInVariants={usedInVariants}
                      gxData={gxData}
                      gxLoading={gxLoading}
                      gxError={gxError}
                      selectedRatio={selectedRatio}
                      file1x1={file1x1}
                      file4x5={file4x5}
                      file9x16={file9x16}
                      onFile1x1={setFile1x1}
                      onFile4x5={setFile4x5}
                      onFile9x16={setFile9x16}
                      activeFeedSlot={activeFeedSlot}
                      onFeedSlotSelect={setFeedPreviewSlot}
                      onMetaCaptionChange={setMetaCaption}
                      onMetaHeadlineChange={setMetaHeadline}
                      onMetaDescriptionChange={setMetaDescription}
                      onMetaCtaChange={setMetaCta}
                      onMetaDCImageUrl1x1Change={(url) => {
                        setSelectedRatio('1x1');
                        setMetaDCImageUrl1x1(url);
                      }}
                      onMetaDCImageUrl9x16Change={(url) => {
                        setSelectedRatio('9x16');
                        setMetaDCImageUrl9x16(url);
                      }}
                      onMetaDCCaptionChange={setMetaDCCaption}
                      onMetaDCHeadlineChange={setMetaDCHeadline}
                      onMetaDCDescriptionChange={setMetaDCDescription}
                      onMetaDCCtaChange={setMetaDCCta}
                      onMetaDCPrimaryTextsChange={setMetaDCPrimaryTexts}
                      onMetaDCHeadlinesArrayChange={setMetaDCHeadlines}
                      onMetaDCDescriptionsArrayChange={setMetaDCDescriptions}
                      onMetaDCCtasArrayChange={setMetaDCCtas}
                      onMetaDCUrlChange={setMetaDCUrl}
                      onMetaDCLegalChange={setMetaDCLegal}
                      onMetaDCNewFiles1x1Change={(newFiles, deletedIds, order) => {
                        setMetaDCNewFiles1x1(newFiles);
                        setMetaDCDeletedIds1x1(deletedIds);
                        setMetaDCOrder1x1(order);
                      }}
                      onMetaDCNewFiles4x5Change={(newFiles, deletedIds, order) => {
                        setMetaDCNewFiles4x5(newFiles);
                        setMetaDCDeletedIds4x5(deletedIds);
                        setMetaDCOrder4x5(order);
                      }}
                      onMetaDCNewFiles9x16Change={(newFiles, deletedIds, order) => {
                        setMetaDCNewFiles9x16(newFiles);
                        setMetaDCDeletedIds9x16(deletedIds);
                        setMetaDCOrder9x16(order);
                      }}
                      metaDCInitialValues={(() => {
                        // Read text fields directly from the API data (like MetaSingleDetails does)
                        // rather than from parent state, which lags one render behind due to useEffect.
                        // This ensures MetaDynamicCreativeDetails gets fresh values when the formKey
                        // causes a remount after data loads.
                        const va = assetsForVariant(selectedVariant);
                        const primary =
                          va.find((a) => normalizeRatio(a.ratio) === '1x1') ??
                          va.find((a) => normalizeRatio(a.ratio) === '9x16') ??
                          va[0];
                        const apiPrimaryTexts = primary?.post_copies?.filter(Boolean) as
                          | string[]
                          | undefined;
                        const apiHeadlines = primary?.headlines?.filter(Boolean) as
                          | string[]
                          | undefined;
                        const apiDescriptions = primary?.descriptions?.filter(Boolean) as
                          | string[]
                          | undefined;
                        const apiCtas = normalizeCta(primary?.cta);
                        return {
                          primaryTexts: apiPrimaryTexts?.length
                            ? apiPrimaryTexts
                            : metaDCPrimaryTexts,
                          headlines: apiHeadlines?.length ? apiHeadlines : metaDCHeadlines,
                          descriptions: apiDescriptions?.length
                            ? apiDescriptions
                            : metaDCDescriptions,
                          cta: apiCtas.length ? apiCtas : metaDCCtas,
                          url: primary?.url ?? metaDCUrl,
                          legal: primary?.legal ?? metaDCLegal,
                          initial1x1: metaDCInitial1x1,
                          initial4x5: metaDCInitial4x5,
                          initial9x16: metaDCInitial9x16,
                        } satisfies MetaDCInitialValues;
                      })()}
                      onFlexibleImageUrlChange={setFlexibleImageUrl}
                      onFlexibleCaptionChange={setFlexibleCaption}
                      onFlexibleHeadlineChange={setFlexibleHeadline}
                      onFlexibleDescriptionChange={setFlexibleDescription}
                      onFlexibleCtaChange={setFlexibleCta}
                      onFlexibleUrlChange={setFlexibleUrl}
                      onFlexibleLegalChange={setFlexibleLegal}
                      onFlexibleCardsChange={(cards, deletedIds) => {
                        setFlexibleCards(cards);
                        setFlexibleDeletedIds(deletedIds);
                      }}
                      flexibleInitialValues={
                        {
                          cards: flexibleInitialCards,
                          cta: flexibleCta,
                          url: flexibleUrl,
                          legal: flexibleLegal,
                        } satisfies MetaFlexibleInitialValues
                      }
                      onCollectionHeroFileChange={(f) => setFile1x1(f)}
                      onCollectionHeroClear={() => {
                        const hero1x1 = assetsForVariant(selectedVariant)
                          .filter((a) => normalizeRatio(a.ratio) === '1x1' && !!a.storage_path)
                          .sort(byDisplayOrder)[0];
                        if (hero1x1) setPendingDeleteAssetIds((prev) => [...prev, hero1x1.id]);
                        setFile1x1(null);
                      }}
                      onCollectionCaptionChange={setCollectionCaption}
                      onCollectionCtaChange={setCollectionCta}
                      onCollectionProductsChange={setCollectionProducts}
                      onCollectionProductFilesChange={(files, deletedIds) => {
                        setCollectionProductFiles(files);
                        setCollectionProductDeletedIds(deletedIds);
                      }}
                      onCollectionWebsiteChange={setCollectionWebsite}
                      onCollectionUrlChange={setCollectionUrl}
                      onCollectionLegalChange={setCollectionLegal}
                      collectionInitialValues={{
                        caption: collectionCaption,
                        url: collectionUrl,
                        cta: collectionCta,
                        legal: collectionLegal,
                        existingHeroUrl: apiImageUrl1x1,
                        heroContentType: contentType1x1,
                        products: collectionInitialProducts,
                      }}
                      metaCarouselCaption={metaCarouselCaption}
                      onMetaCarouselCaptionChange={setMetaCarouselCaption}
                      metaCarouselDestinationUrl={metaCarouselDestinationUrl}
                      onMetaCarouselDestinationUrlChange={setMetaCarouselDestinationUrl}
                      metaCarouselCta={metaCarouselCta}
                      onMetaCarouselCtaChange={setMetaCarouselCta}
                      metaCarouselLegal={metaCarouselLegal}
                      onMetaCarouselLegalChange={setMetaCarouselLegal}
                      metaCarouselFeedActiveCard={metaCarouselFeedActiveCard}
                      metaCarouselStoryActiveCard={metaCarouselStoryActiveCard}
                      onMetaCarouselFeedItemsChange={setMetaCarouselFeedItems}
                      onMetaCarouselStoryItemsChange={setMetaCarouselStoryItems}
                      metaCarouselFeedSlot={metaCarouselFeedSlot}
                      onMetaCarouselSelectFeedCard={(i, ratio) => {
                        // selectedRatio stays '1x1' for both crops — it drives which
                        // mockup is mounted, and 1:1 and 4:5 share one. Which crop that
                        // mockup renders is metaCarouselFeedSlot's job.
                        setSelectedRatio('1x1');
                        setMetaCarouselFeedSlot(ratio);
                        setMetaCarouselFeedActiveCard(i);
                      }}
                      onMetaCarouselSelectStoryCard={(i) => {
                        setSelectedRatio('9x16');
                        setMetaCarouselStoryActiveCard(i);
                      }}
                      onMetaCarouselFeedCardsChange={handleMetaCarouselFeedCardsChange}
                      onMetaCarouselStoryCardsChange={handleMetaCarouselStoryCardsChange}
                      metaCarouselFeedInitialItems={
                        metaCarouselFeedResolvedCards.length > 0
                          ? metaCarouselFeedResolvedCards
                          : undefined
                      }
                      metaCarouselStoryInitialItems={
                        metaCarouselStoryResolvedCards.length > 0
                          ? metaCarouselStoryResolvedCards
                          : undefined
                      }
                      onSnapchatHeadlineChange={setSnapchatHeadline}
                      onSnapchatUrlChange={setSnapchatUrl}
                      onSnapchatLegalChange={setSnapchatLegal}
                      onSnapchatExistingAssetClear={() => {
                        const asset = assetsForVariant(selectedVariant).find(
                          (a) => normalizeRatio(a.ratio) === '9x16',
                        );
                        if (asset)
                          setPendingDeleteAssetIds((prev) => [
                            ...prev.filter((id) => id !== asset.id),
                            asset.id,
                          ]);
                      }}
                      snapchatActiveCard={snapchatActiveCard}
                      onSnapchatStorySelectCard={setSnapchatActiveCard}
                      snapchatStoryHeadline={snapchatStoryHeadline}
                      onSnapchatStoryHeadlineChange={setSnapchatStoryHeadline}
                      snapchatStoryUrl={snapchatStoryUrl}
                      onSnapchatStoryUrlChange={setSnapchatStoryUrl}
                      snapchatStoryLegal={snapchatStoryLegal}
                      onSnapchatStoryLegalChange={setSnapchatStoryLegal}
                      onSnapchatStoryItemsChange={setSnapchatStoryItems}
                      onSnapchatStoryCardsChange={(newFiles, deletedIds, totalCount, order) => {
                        setSnapchatStoryNewFiles(newFiles);
                        setSnapchatStoryDeletedIds(deletedIds);
                        setSnapchatStoryTotalCount(totalCount);
                        setSnapchatStoryOrder(order);
                      }}
                      snapchatStoryInitialItems={
                        snapchatStoryResolvedCards.length > 0
                          ? snapchatStoryResolvedCards
                          : undefined
                      }
                      linkedInFile={linkedInFile}
                      onLinkedInFileChange={setLinkedInFile}
                      linkedInFile4x5={linkedInFile4x5}
                      onLinkedInFile4x5Change={setLinkedInFile4x5}
                      linkedInExistingImageUrl4x5={apiImageUrl4x5}
                      activeLinkedInSlot={activeLinkedInSlot}
                      onLinkedInSlotSelect={setFeedPreviewSlot}
                      linkedInCaption={linkedInCaption}
                      onLinkedInCaptionChange={setLinkedInCaption}
                      linkedInHeadline={linkedInHeadline}
                      onLinkedInHeadlineChange={setLinkedInHeadline}
                      linkedInCta={linkedInCta}
                      onLinkedInCtaChange={setLinkedInCta}
                      onLinkedInWebsiteChange={setLinkedInWebsite}
                      onLinkedInUrlChange={setLinkedInUrl}
                      onLinkedInLegalChange={setLinkedInLegal}
                      onLinkedInExistingImageClear={(ratio: UploadRatio) => {
                        // exactRatio, not normalizeRatio: both slots normalise to
                        // '1x1', so normalizing would queue whichever row came
                        // first and delete the wrong asset.
                        const asset = assetsForVariant(selectedVariant).find(
                          (a) => exactRatio(a.ratio) === ratio,
                        );
                        if (asset)
                          setPendingDeleteAssetIds((prev) => [
                            ...prev.filter((id) => id !== asset.id),
                            asset.id,
                          ]);
                      }}
                      linkedInDocFile={linkedInDocFile}
                      onLinkedInDocFileChange={setLinkedInDocFile}
                      linkedInDocInitialPdfUrl={
                        mockupType === 'linkedin-document' ? apiImageUrl1x1 : undefined
                      }
                      linkedInDocInitialPageImageUrl={
                        mockupType === 'linkedin-document'
                          ? (linkedInDocPageImage ?? undefined)
                          : undefined
                      }
                      onLinkedInDocExistingPdfClear={() => {
                        const asset = assetsForVariant(selectedVariant).find(
                          (a) => normalizeRatio(a.ratio) === '1x1',
                        );
                        if (asset)
                          setPendingDeleteAssetIds((prev) => [
                            ...prev.filter((id) => id !== asset.id),
                            asset.id,
                          ]);
                      }}
                      linkedInDocCaption={linkedInDocCaption}
                      onLinkedInDocCaptionChange={setLinkedInDocCaption}
                      linkedInDocHeadline={linkedInDocHeadline}
                      onLinkedInDocHeadlineChange={setLinkedInDocHeadline}
                      onLinkedInDocPageImageChange={setLinkedInDocPageImage}
                      onLinkedInDocUrlChange={setLinkedInDocUrl}
                      onLinkedInDocLegalChange={setLinkedInDocLegal}
                      linkedInCarouselCaption={linkedInCarouselCaption}
                      onLinkedInCarouselCaptionChange={setLinkedInCarouselCaption}
                      linkedInCarouselLegal={linkedInCarouselLegal}
                      onLinkedInCarouselLegalChange={setLinkedInCarouselLegal}
                      linkedInCarouselActiveCard={linkedInCarouselActiveCard}
                      linkedInCarouselFeedSlot={linkedInCarouselFeedSlot}
                      onLinkedInCarouselSelectCard={(index, ratio) => {
                        setLinkedInCarouselActiveCard(index);
                        setLinkedInCarouselFeedSlot(ratio);
                      }}
                      onLinkedInCarouselItemsChange={setLinkedInCarouselItems}
                      onLinkedInCarouselCardsChange={(
                        uploads,
                        deletedIds,
                        totalCount,
                        cardData,
                      ) => {
                        setLinkedInCarouselNewFiles(uploads['1x1'].newFiles);
                        setLinkedInCarouselOrder(uploads['1x1'].order);
                        setLinkedInCarousel4x5NewFiles(uploads['4x5'].newFiles);
                        setLinkedInCarousel4x5Order(uploads['4x5'].order);
                        setLinkedInCarouselDeletedIds(deletedIds);
                        setLinkedInCarouselTotalCount(totalCount);
                        setLinkedInCarouselCardData(cardData);
                      }}
                      linkedInCarouselInitialItems={
                        linkedInCarouselResolvedCards.length > 0
                          ? linkedInCarouselResolvedCards
                          : undefined
                      }
                      onTikTokAdTextChange={setTikTokCaption}
                      onTikTokCtaChange={setTikTokCta}
                      onTikTokUrlChange={setTikTokUrl}
                      onTikTokLegalChange={setTikTokLegal}
                      onTikTokExistingVideoClear={() => {
                        const asset = assetsForVariant(selectedVariant).find(
                          (a) => normalizeRatio(a.ratio) === '9x16',
                        );
                        if (asset)
                          setPendingDeleteAssetIds((prev) => [
                            ...prev.filter((id) => id !== asset.id),
                            asset.id,
                          ]);
                      }}
                      onMetaPostCopiesChange={setMetaAllPostCopies}
                      onMetaUrlChange={setMetaUrl}
                      onMetaLegalChange={setMetaLegal}
                      onMetaExistingImageClear={(ratio) => {
                        // exactRatio: normalizeRatio would match a 4:5 row when the
                        // user cleared the 1:1 slot, queueing the wrong deletion.
                        const asset = assetsForVariant(selectedVariant).find(
                          (a) => exactRatio(a.ratio) === ratio,
                        );
                        if (asset)
                          setPendingDeleteAssetIds((prev) => [
                            ...prev.filter((id) => id !== asset.id),
                            asset.id,
                          ]);
                      }}
                      // formKey causes a remount whenever variant, assets, or resolved image URLs change,
                      // so the form always re-reads initialValues with the latest data.
                      key={formKey}
                      initialValues={(() => {
                        const va = assetsForVariant(selectedVariant);
                        const primary =
                          va.find((a) => exactRatio(a.ratio) === '1x1') ??
                          va.find((a) => exactRatio(a.ratio) === '4x5') ??
                          va.find((a) => normalizeRatio(a.ratio) === '9x16') ??
                          va[0];
                        if (!primary) return undefined;
                        const asset4x5 = va.find((a) => exactRatio(a.ratio) === '4x5');
                        const asset9x16 = va.find((a) => normalizeRatio(a.ratio) === '9x16');
                        return {
                          postCopies: primary.post_copies?.filter(Boolean) as string[] | undefined,
                          headline: primary.headlines?.[0] ?? undefined,
                          description: primary.descriptions?.[0] ?? undefined,
                          cta: normalizeCta(primary.cta)[0] ?? undefined,
                          url: primary.url ?? undefined,
                          legal: primary.legal ?? undefined,
                          // Use the already-resolved blob/signed URLs rather than raw /content paths,
                          // so AssetPreviewFromUrl gets a URL it can render without auth headers.
                          existingImageUrl1x1: apiImageUrl1x1,
                          existingImageUrl4x5: apiImageUrl4x5,
                          existingImageUrl9x16: apiImageUrl9x16,
                          imageContentType: primary.content_type ?? undefined,
                          imageContentType4x5: asset4x5?.content_type ?? undefined,
                          imageContentType9x16: asset9x16?.content_type ?? undefined,
                        };
                      })()}
                    />
                  </fieldset>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Upload Progress Modal */}
      <UploadProgressModal
        uploadProgress={uploadProgress}
        isUploading={isUploading}
        show={showUploadProgress}
        onCancel={cancelUpload}
        onDismiss={dismissUpload}
        busy={isValidatingLocal}
      />
    </>
  );
}
